import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import CounterAnimation from '../components/CounterAnimation';
import FloatingParticles from '../components/FloatingParticles';
import PartnerCategoryModal from '../components/PartnerCategoryModal';
import { instagramService } from '../services/instagramService';
import { blogService } from '../services/blogService';
import { studioContent } from '../data/studioContent';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';
gsap.registerPlugin(ScrollTrigger);

const HomePage = () => {
    const navigate = useNavigate();
    const [instagramPosts, setInstagramPosts] = useState([]);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [activeProject, setActiveProject] = useState(0);
    const [activeService, setActiveService] = useState('planning');
    const [activeTab, setActiveTab] = useState('blogs');
    const [isLoading, setIsLoading] = useState(true);
    const [showStudioPopup, setShowStudioPopup] = useState(false);
    const [selectedStudio, setSelectedStudio] = useState(null);
    const [showPartnerPopup, setShowPartnerPopup] = useState(false);
    const [showPartnerCategoryModal, setShowPartnerCategoryModal] = useState(false);
    const [selectedPartnerCategory, setSelectedPartnerCategory] = useState(null);
    const [activeExperienceTab, setActiveExperienceTab] = useState('cafe');
    const [partnerForm, setPartnerForm] = useState({
        name: '',
        phone: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    
    // Calculator state
    const [selectedHouseType, setSelectedHouseType] = useState(null);
    const [squareFeet, setSquareFeet] = useState('');
    const [totalBudget, setTotalBudget] = useState('');
    const [calculatedBreakdown, setCalculatedBreakdown] = useState(null);
    
    const heroRef = useRef(null);

    // House type data for calculator
    const houseTypes = [
        { id: '1bhk', name: '1 BHK Apartment', sqft: '400-600 sq.ft', range: '₹8-15 Lakhs', defaultSqft: 500, defaultBudget: 12 },
        { id: '2bhk', name: '2 BHK Apartment', sqft: '600-1000 sq.ft', range: '₹15-30 Lakhs', defaultSqft: 800, defaultBudget: 22 },
        { id: '3bhk', name: '3 BHK Apartment', sqft: '1000-1500 sq.ft', range: '₹30-60 Lakhs', defaultSqft: 1250, defaultBudget: 45 },
        { id: '4bhk', name: '4 BHK Villa', sqft: '2000-3000 sq.ft', range: '₹60-100 Lakhs', defaultSqft: 2500, defaultBudget: 80 },
        { id: 'luxury', name: 'Luxury Villa', sqft: '3000-5000 sq.ft', range: '₹100-200 Lakhs', defaultSqft: 4000, defaultBudget: 150 },
        { id: 'independent', name: 'Independent House', sqft: '1500-2500 sq.ft', range: '₹45-80 Lakhs', defaultSqft: 2000, defaultBudget: 62 }
    ];



    // Google Analytics
    useEffect(() => {

        // Load Google Analytics script
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-ZGXM8W2JSV';
        document.head.appendChild(script1);

        // Initialize Google Analytics
        const script2 = document.createElement('script');
        script2.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', 'G-ZGXM8W2JSV');
        `;
        document.head.appendChild(script2);

    }, []);


    // Budget breakdown categories
    const budgetCategories = [
        { name: 'Civil Modifications (Partition Walls, etc.)', percentage: 6, covered: false },
        { name: 'False Ceiling & POP Works', percentage: 8, covered: true },
        { name: 'Painting & Wall Finishes (Texture, Wallpapers)', percentage: 7, covered: true },
        { name: 'Flooring (Tiles, Wooden, Stone Inlays)', percentage: 10, covered: true },
        { name: 'Electrical Work & Lighting', percentage: 10, covered: false },
        { name: 'Plumbing Alteration & Sanitary Fixtures', percentage: 5, covered: false },
        { name: 'Modular Kitchen', percentage: 8, covered: true },
        { name: 'Wardrobes & Storage Units', percentage: 10, covered: true },
        { name: 'Loose Furniture (Beds, Sofas, Dining, etc.)', percentage: 10, covered: true },
        { name: 'Soft Furnishing (Curtains, Blinds, Rugs)', percentage: 5, covered: true },
        { name: 'Appliances (AC, Chimney, Hob, etc.)', percentage: 5, covered: true },
        { name: 'Décor, Art, Mirrors, Accessories', percentage: 4, covered: true },
        { name: 'Project Management & Design Fees', percentage: 6, covered: true },
        { name: 'Contingency / Buffer', percentage: 6, covered: false }
    ];

    // Calculate budget breakdown
    const calculateBudget = () => {
        if (!totalBudget || totalBudget <= 0) {
            alert('Please enter a valid budget amount');
            return;
        }
        
        const breakdown = budgetCategories.map(category => ({
            ...category,
            cost: ((category.percentage / 100) * parseFloat(totalBudget)).toFixed(2)
        }));
        
        const totalCovered = breakdown
            .filter(item => item.covered)
            .reduce((sum, item) => sum + parseFloat(item.cost), 0);
        
        setCalculatedBreakdown({
            items: breakdown,
            totalCovered: totalCovered.toFixed(2),
            coveragePercentage: 73
        });
    };

    // Handle house type selection
    const handleHouseTypeSelect = (houseType) => {
        setSelectedHouseType(houseType.id);
        setSquareFeet(houseType.defaultSqft);
        setTotalBudget(houseType.defaultBudget);
        setCalculatedBreakdown(null);
    };

    // Dummy Instagram data as fallback
    const dummyInstagramPosts = [
        {
            id: 'dummy1',
            imageUrl: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800',
            caption: 'Luxury marble installation for a modern villa. Timeless elegance meets contemporary design. #HAVNInteriors #MarbleDesign #LuxuryInteriors',
            likes: 1245,
            timestamp: new Date('2025-03-10')
        },
        {
            id: 'dummy2',
            imageUrl: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800',
            caption: 'Creating stunning ceiling designs with integrated lighting. Transform your space from above. #CeilingDesign #Aether #InteriorDesign',
            likes: 892,
            timestamp: new Date('2025-03-08')
        },
        {
            id: 'dummy3',
            imageUrl: 'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800',
            caption: 'Custom woodwork that tells a story. Every detail matters in creating spaces that inspire. #Chisel #Woodwork #CustomFurniture',
            likes: 1567,
            timestamp: new Date('2025-03-05')
        },
        {
            id: 'dummy4',
            imageUrl: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
            caption: 'Premium fabrics and textiles that add warmth and character to any space. #Thread #Textiles #SoftFurnishing',
            likes: 743,
            timestamp: new Date('2025-03-02')
        },
        {
            id: 'dummy5',
            imageUrl: 'https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=800',
            caption: 'Bringing visions to life with our comprehensive design solutions. Your dream space awaits. #HAVNInteriors #InteriorDesign #LuxuryLiving',
            likes: 2103,
            timestamp: new Date('2025-02-28')
        },
        {
            id: 'dummy6',
            imageUrl: 'https://images.unsplash.com/photo-1615874959474-d609969a20ed?w=800',
            caption: 'Flooring that foundations your design story. From marble to hardwood, we source the finest. #Strata #Flooring #InteriorMaterials',
            likes: 956,
            timestamp: new Date('2025-02-25')
        }
    ];

    // Dummy blog data as fallback
    const dummyBlogs = [
        {
            _id: 'blog1',
            title: 'Top Interior Design Trends for 2025',
            slug: 'top-interior-design-trends-2025',
            excerpt: 'Discover the latest trends shaping modern interior design, from sustainable materials to smart home integration.',
            featuredImage: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800',
            createdAt: new Date('2025-10-06'),
            published: true
        },
        {
            _id: 'blog2',
            title: 'Maximizing Small Spaces: Expert Tips',
            slug: 'maximizing-small-spaces-expert-tips',
            excerpt: 'Learn how to transform compact areas into functional, stylish living spaces with our professional design strategies.',
            featuredImage: 'https://images.unsplash.com/photo-1615529182904-14819c35db37?w=800',
            createdAt: new Date('2025-10-03'),
            published: true
        },
        {
            _id: 'blog3',
            title: 'Choosing the Perfect Color Palette',
            slug: 'choosing-perfect-color-palette',
            excerpt: 'A comprehensive guide to selecting colors that reflect your personality and enhance your living environment.',
            featuredImage: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800',
            createdAt: new Date('2025-09-28'),
            published: true
        },
        {
            _id: 'blog4',
            title: 'Sustainable Interior Design Practices',
            slug: 'sustainable-interior-design-practices',
            excerpt: 'Explore eco-friendly materials and sustainable practices that make your interiors beautiful and responsible.',
            featuredImage: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800',
            createdAt: new Date('2025-09-25'),
            published: true
        },
        {
            _id: 'blog5',
            title: 'The Art of Lighting Design',
            slug: 'art-of-lighting-design',
            excerpt: 'Understand how strategic lighting can transform your space and create the perfect ambiance for every room.',
            featuredImage: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800',
            createdAt: new Date('2025-09-20'),
            published: true
        },
        {
            _id: 'blog6',
            title: 'Luxury on a Budget: Smart Shopping Tips',
            slug: 'luxury-on-budget-smart-shopping-tips',
            excerpt: 'Get professional advice on creating high-end looks without breaking the bank through smart sourcing and planning.',
            featuredImage: 'https://images.unsplash.com/photo-1556912173-46c336c7fd55?w=800',
            createdAt: new Date('2025-09-15'),
            published: true
        }
    ];

    // Hero images for rotation
    const heroImages = [
        "https://cdn.prod.website-files.com/677276fd561b48d392692dc8/677276fd561b48d392692e3d_STONE-min.png",
        "https://cdn.prod.website-files.com/677276fd561b48d392692dc8/677276fd561b48d392692e3a_WOOD-min.png",
        "https://cdn.prod.website-files.com/677276fd561b48d392692dc8/677276fd561b48d392692e3c_FABRIC-min.png",
        "https://cdn.prod.website-files.com/677276fd561b48d392692dc8/677276fd561b48d392692e3b_LEATHER-min.png",
        "https://cdn.prod.website-files.com/677276fd561b48d392692dc8/677276fd561b48d392692e39_METAL-min.png"
    ];

    // Projects data - Aligned with 8 Design Studios
    const projects = [
        { id: 0, title: "LumiCeiling", category: "Studio • Ceiling Design", image: `${process.env.PUBLIC_URL}/images/studios/lumiceiling.jpg`, description: "At LumiCeiling, we believe the ceiling is the fifth wall of every beautiful space. Our studio offers high-end ceiling design services that combine innovation, lighting, texture, and architectural elegance to create visually striking interiors. Whether residential or commercial, we design ceilings that leave a lasting impression." },
        { id: 1, title: "LocknKey", category: "Studio • Hardware & Fixtures", image: `${process.env.PUBLIC_URL}/images/studios/locknkey.jpg`, description: "At LocknKey, we believe that the finest details create the most luxurious spaces. Our studio offers high-end hardware and fixture solutions designed to complement modern architecture and elegant interiors. With a commitment to quality, innovation, and timeless style, we provide products that transform ordinary spaces into exceptional experiences." },
        { id: 2, title: "ShapeStudio", category: "Studio • Stone & Marble", image: `${process.env.PUBLIC_URL}/images/studios/shapestudio.jpg`, description: "At ShapeStudio, we bring timeless beauty and strength to architectural spaces through premium stone and marble solutions. From luxurious marble surfaces to elegant natural stone finishes, we provide materials that enhance the character and sophistication of every building. Our focus is on quality, craftsmanship, and delivering surfaces that leave a lasting impression." },
        { id: 3, title: "CraftCove", category: "Studio • Wood & Doors", image: `${process.env.PUBLIC_URL}/images/studios/craftcove.jpg`, description: "CraftCove is a premium woodworking studio specializing in luxurious wooden interiors and custom-crafted solutions. We provide beautifully designed doors, windows, wardrobes, paneling, and all types of wooden work that combine functionality with sophisticated style. With expert craftsmanship and premium materials, we help create spaces that feel refined, elegant, and lasting." },
        { id: 4, title: "Thread", category: "Studio • Textiles & Soft Furnishing", image: `${process.env.PUBLIC_URL}/images/studios/thread.jpg`, description: "Thread is a premium soft furnishing and textile studio dedicated to transforming spaces with rich textures, elegant fabrics, and modern design aesthetics. We specialize in high-quality curtains, rugs, mats, upholstery, and custom furnishing solutions that add comfort and character to every interior. With a focus on quality and detail, we help create spaces that feel truly luxuriou" },
        { id: 5, title: "SurfaceStyle", category: "Studio • Flooring Solutions", image: `${process.env.PUBLIC_URL}/images/studios/surfacestyle.jpg`, description: "SurfaceStyle is a premium flooring studio dedicated to transforming buildings and interiors with sophisticated surface solutions. We specialize in high-quality flooring systems designed to enhance the beauty, comfort, and performance of residential and commercial spaces. With attention to detail and timeless aesthetics, we deliver flooring experiences that reflect luxury and refinement." },
        { id: 6, title: "GlobalNest", category: "Studio • Global Furniture", image: `${process.env.PUBLIC_URL}/images/studios/globalnest.jpg`, description: "GlobalNest is a premium furniture studio dedicated to bringing beautifully crafted and thoughtfully designed furniture to modern spaces. We offer elegant and high-quality furniture solutions that combine contemporary design with lasting durability. Our mission is to create interiors that reflect sophistication, comfort, and individuality." },
        { id: 7, title: "ArtistryRoom", category: "Studio • Decorative Elements", image: `${process.env.PUBLIC_URL}/images/studios/artistryroom.jpg`, description: "ArtistryRoom is a luxury décor studio dedicated to enhancing interiors with beautifully crafted decorative items and timeless design aesthetics. We offer premium décor solutions that blend creativity, elegance, and modern style to elevate homes, offices, and commercial spaces. Every piece is selected to create a refined and unforgettable ambiance." }
    ];

    // Services data
    const services = {
        planning: {
            title: "Space Planning",
            description: "Strategic space utilization for functional and aesthetic excellence.",
            features: ["3D Layout Design", "Traffic Flow Analysis", "Zoning & Functionality", "Ergonomic Planning"]
        },
        lumi: {
            title: "Ceiling Design",
            description: "Transforming ceilings into architectural masterpieces.",
            features: ["False Ceiling Systems", "Lighting Integration", "Acoustic Solutions", "Decorative Elements"]
        },
        craftcove: {
            title: "Woodwork & Doors",
            description: "Precision crafted woodwork and door solutions.",
            features: ["Custom Paneling", "Designer Doors", "Built-in Furniture", "Veneer Applications"]
        },
        thread: {
            title: "Textile & Soft Furnishing",
            description: "Premium fabrics and soft furnishings for comfort and style.",
            features: ["Custom Curtains", "Upholstery", "Cushions & Throws", "Wall Fabrics"]
        },
        artistry: {
            title: "Decorative Elements",
            description: "Curated décor pieces that complete your space.",
            features: ["Art & Sculptures", "Decorative Lighting", "Mirrors & Frames", "Accent Pieces"]
        },
        globalnest: {
            title: "Furniture Sourcing",
            description: "Global furniture sourcing and custom OEM/ODM solutions.",
            features: ["International Brands", "Custom Manufacturing", "Quality Assurance", "Timely Delivery"]
        },
        management: {
            title: "Project Management",
            description: "End-to-end project execution with precision and care.",
            features: ["Timeline Management", "Vendor Coordination", "Quality Control", "Budget Management"]
        }
    };

    // Studio content imported from data/studioContent.js

    // Segments data
    const segments = [
        {
            number: "01",
            slug: "specialized-industrial-interiors",
            title: "Specialized & Industrial Interiors",
            description: "This segment includes properties with highly specialized functional requirements, often involving specific equipment, environmental controls, or operational efficiency.",
            count: "8 Specializations",
            items: [
                "Warehouses & Industrial Units",
                "Research & Development Facilities",
                "Manufacturing Units & Production Facilities",
                "Data Centers & IT Facilities",
                "Logistics & Distribution Centers",
                "Spas & Wellness Centers",
                "Wellness Retreats (focusing on interior experience for specific services)",
                "Cruise Ships & Resorts (large-scale hospitality with complex, specialized design needs)"
            ]
        },
        {
            number: "02",
            title: "Institutional & Public Sector Interiors",
            description: "This category involves designing spaces for public services, education, healthcare, government, and large-scale community functions, often with specific regulations, accessibility, and high-traffic considerations.",
            count: "14 Specializations",
            items: [
                "Hospitals & Healthcare Facilities",
                "Educational Institutions (Schools, Colleges)",
                "Airports & Transportation Hubs",
                "Government Buildings",
                "Banks & Financial Institutions",
                "Public Sector Projects",
                "Religious & Cultural Institutions",
                "Conference & Convention Centers",
                "Medical & Dental Clinics",
                "Community Centers",
                "Government Offices & Administrative Buildings",
                "Sports & Recreational Facilities",
                "Public Parks & Recreational Spaces (design of associated buildings/structures)"
            ]
        },
        {
            number: "03",
            title: "Commercial & Retail Interiors",
            description: "This segment focuses on spaces designed for business operations, sales, and customer interaction, emphasizing branding, workflow, and customer experience.",
            count: "18 Specializations",
            items: [
                "Offices & Corporate Offices",
                "Co-working Spaces",
                "Retail Showrooms",
                "Restaurants & Cafés",
                "Commercial Real Estate",
                "Retail Stores & Showrooms",
                "Retail Chains & Malls",
                "Showrooms (Automotive, Appliances)",
                "Shopping Complexes",
                "Law Firms & Legal Offices",
                "Corporate Headquarters",
                "Exhibition Halls",
                "Mobile Offices & Temporary Spaces",
                "Studios (Photography, Music, Art) - when designed for commercial use",
                "Cinemas & Entertainment Complexes",
                "Theme Parks & Entertainment Venues"
            ]
        },
        {
            number: "04",
            title: "Residential Interiors",
            description: "This category encompasses all spaces primarily designed for living, comfort, and personal expression. Interior services here focus on creating functional, aesthetically pleasing, and personalized homes.",
            count: "11 Specializations",
            items: [
                "Farmhouses",
                "Villas & Luxury Villas",
                "Independent Houses",
                "Residential Construction",
                "Luxury Apartments",
                "High-end Residential Units",
                "Senior Living & Retirement Homes",
                "Agricultural Projects (Farmhouses, Greenhouses)",
                "Home Automation Systems (installation and integration within residential spaces)",
                "Libraries & Learning Centers (within a residential context, e.g., home libraries)",
                "Pet Care Facilities (home-based or small, personalized services)"
            ]
        }
    ];

    // Studio popup handlers
    const handleStudioClick = (studioKey) => {
        setSelectedStudio(studioKey);
        setShowStudioPopup(true);
    };

    const closeStudioPopup = () => {
        setShowStudioPopup(false);
        setSelectedStudio(null);
    };

    const handleSegmentClick = (segmentNumber) => {
        // Navigate to segments page with state to trigger scroll
        navigate('/segments', { 
            state: { 
                scrollToSegment: `segment-${segmentNumber}` 
            } 
        });
    };

    useEffect(() => {
        // Simulate loading
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);

        // Fetch data
        instagramService.getPosts()
            .then(data => {
                // Use real data if available, otherwise use dummy data
                if (data.posts && data.posts.length > 0) {
                    setInstagramPosts(data.posts);
                } else {
                    setInstagramPosts(dummyInstagramPosts);
                }
            })
            .catch(err => {
                console.log('Instagram not linked, using dummy data');
                setInstagramPosts(dummyInstagramPosts);
            });

        blogService.getAllBlogs()
            .then(data => {
                // Use real data if available and has at least 6 blogs, otherwise supplement with dummy data
                if (data && data.length >= 6) {
                    setLatestBlogs(data.slice(0, 6));
                } else if (data && data.length > 0) {
                    // Mix real and dummy data to get 6 blogs
                    const combined = [...data, ...dummyBlogs].slice(0, 6);
                    setLatestBlogs(combined);
                } else {
                    // Use all dummy data if no real blogs
                    setLatestBlogs(dummyBlogs);
                }
            })
            .catch(err => {
                console.log('Error fetching blogs, using dummy data');
                setLatestBlogs(dummyBlogs);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isLoading) return;

        // Hero animations
        const heroTitle = heroRef.current.querySelectorAll('.split-line span');
        const heroSubtitle = heroRef.current.querySelector('.hero-subtitle');
        const heroDescription = heroRef.current.querySelector('.hero-content p:nth-of-type(2)');
        const heroButtons = heroRef.current.querySelectorAll('.hero-content a');
        const heroStats = heroRef.current.querySelectorAll('.hero-content > div:last-child > div');
        const scrollIndicator = heroRef.current.querySelector('.scroll-indicator');
        const floatingImages = heroRef.current.querySelectorAll('.floating-material-img');

        // Animate title
        gsap.fromTo(heroTitle, 
            { y: '120%', opacity: 0 },
            { y: '0%', opacity: 1, duration: 1.2, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
        );

        // Animate subtitle
        gsap.fromTo(heroSubtitle,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, delay: 1.0, ease: 'power2.out' }
        );

        // Animate description
        if (heroDescription) {
            gsap.fromTo(heroDescription,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.8, delay: 1.3, ease: 'power2.out' }
            );
        }

        // Animate buttons
        gsap.fromTo(heroButtons,
            { opacity: 0, y: 20, scale: 0.9 },
            { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.1, delay: 1.6, ease: 'back.out(1.7)' }
        );

        // Animate stats
        gsap.fromTo(heroStats,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, delay: 2.0, ease: 'power2.out' }
        );

        // Animate scroll indicator
        gsap.fromTo(scrollIndicator,
            { opacity: 0 },
            { opacity: 1, duration: 1, delay: 2.5, ease: 'power2.out' }
        );

        // Animate floating material images with rotation
        gsap.fromTo(floatingImages,
            { opacity: 0, scale: 0, rotation: -180 },
            { 
                opacity: 0.9, 
                scale: 1, 
                rotation: 0,
                duration: 1.5, 
                stagger: 0.3, 
                delay: 1.8, 
                ease: 'elastic.out(1, 0.5)' 
            }
        );

        // Continuous rotation animation for floating images
        floatingImages.forEach((img, index) => {
            gsap.to(img, {
                rotation: index % 2 === 0 ? 360 : -360,
                duration: 20 + (index * 5),
                repeat: -1,
                ease: 'none',
                delay: 3
            });
        });

        // Scroll animations
        gsap.utils.toArray('.section-title').forEach(title => {
            gsap.fromTo(title,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    scrollTrigger: {
                        trigger: title,
                        start: 'top 80%',
                        end: 'top 50%',
                        scrub: 1
                    }
                }
            );
        });

        // Add reveal animations for sections
        gsap.utils.toArray('section').forEach((section, index) => {
            if (section.id !== 'home') { // Skip hero section
                gsap.fromTo(section,
                    { opacity: 0, y: 80 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: section,
                            start: 'top 85%',
                            toggleActions: 'play none none reverse'
                        }
                    }
                );
            }
        });

        // Animate cards with stagger effect
        gsap.utils.toArray('.stagger-child').forEach((element) => {
            gsap.fromTo(element,
                { opacity: 0, y: 60, scale: 0.95 },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: element,
                        start: 'top 90%',
                        toggleActions: 'play none none reverse'
                    }
                }
            );
        });

        // Image zoom effect on scroll
        gsap.utils.toArray('.zoom-on-scroll').forEach((container) => {
            const img = container.querySelector('img');
            if (img) {
                gsap.fromTo(img,
                    { scale: 1.2 },
                    {
                        scale: 1,
                        duration: 1.5,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: container,
                            start: 'top 80%',
                            toggleActions: 'play none none reverse'
                        }
                    }
                );
            }
        });

        // Parallax effect for background images
        gsap.utils.toArray('[style*="background"]').forEach((section) => {
            gsap.to(section, {
                backgroundPosition: '50% 100%',
                ease: 'none',
                scrollTrigger: {
                    trigger: section,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true
                }
            });
        });

        // Studio Selector animations
        const studioCircles = document.querySelectorAll('.studio-circle');
        const centerCircle = document.querySelector('.studio-circle.center');
        
        if (centerCircle) {
            gsap.to(centerCircle, {
                opacity: 1,
                scale: 1,
                duration: 0.8,
                ease: 'back.out(1.7)',
                scrollTrigger: {
                    trigger: '.studio-selector',
                    start: 'top 70%'
                }
            });

            const surroundingCircles = Array.from(studioCircles).filter(circle => !circle.classList.contains('center'));
            surroundingCircles.forEach((circle, index) => {
                gsap.to(circle, {
                    opacity: 1,
                    scale: 1,
                    duration: 0.6,
                    delay: 0.5 + (index * 0.1),
                    ease: 'back.out(1.7)',
                    scrollTrigger: {
                        trigger: '.studio-selector',
                        start: 'top 70%'
                    }
                });
            });
        }

        gsap.utils.toArray('.contact-item').forEach((item, index) => {
            gsap.fromTo(item,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    delay: index * 0.1,
                    scrollTrigger: {
                        trigger: item,
                        start: 'top 85%'
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [isLoading]);

    // Auto-play projects slider
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveProject((prev) => (prev === projects.length - 1 ? 0 : prev + 1));
        }, 4000); // Change slide every 4 seconds

        return () => clearInterval(interval);
    }, []);


    return (
        <div>
            <Helmet>
                <title>Hav'n Interiors - Luxury Interior Design & Global Sourcing</title>
                <meta name="description" content="Chennai's premier interior design destination. Experience world-class design through our 8 specialized studios and global sourcing network." />
                <meta name="keywords" content="interior design, luxury interiors, Chennai, global sourcing, furniture, home decor" />
            </Helmet>

            <Navbar />

            {/* Hero Section - Video Background with Animated Content */}
            <section className="hero" id="home" ref={heroRef} style={{ 
                position: 'relative', 
                minHeight: '100vh',
                height: 'auto',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingTop: '0px',
                paddingBottom: '0px',
                zIndex: 1,
                background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #0F0F0F 100%)'
            }}>
                {/* Video Background */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    zIndex: 0
                }}>
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        onError={(e) => {
                            console.log('Video failed to load, using fallback background');
                            e.target.style.display = 'none';
                        }}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            minWidth: '100%',
                            minHeight: '100%',
                            width: 'auto',
                            height: 'auto',
                            transform: 'translate(-50%, -50%)',
                            objectFit: 'cover',
                            opacity: 0.7
                        }}
                    >
                        <source src="/videos/hero-video.mp4" type="video/mp4" />
                        <source src="https://cdn.coverr.co/videos/coverr-elegant-interior-design-showcase-9321/1080p.mp4" type="video/mp4" />
                    </video>
                    {/* Dark overlay for better text readability */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6))',
                        zIndex: 1
                    }}></div>
                </div>

                {/* Floating Particles */}
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }}>
                    <FloatingParticles count={30} />
                </div>

               

                {/* Center Content */}
                <div className="hero-content" style={{
                    position: 'relative',
                    zIndex: 100,
                    textAlign: 'center',
                    maxWidth: '1200px',
                    padding: '0 20px',
                    margin: '0 auto'
                }}>
                    {/* Main Heading with Split Animation */}
                    <h1 style={{
                        fontSize: 'clamp(40px, 8vw, 80px)',
                        fontWeight: '300',
                        color: '#F5F1ED',
                        marginBottom: '20px',
                        lineHeight: '1.1',
                        overflow: 'hidden'
                    }}>
                        <div className="split-line" style={{ overflow: 'hidden', marginBottom: '10px' }}>
                            <span style={{ display: 'inline-block' }}></span>
                        </div>
                        <div className="split-line" style={{ overflow: 'hidden' }}>
                            <span style={{ display: 'inline-block' }}>Interior Mall Of Madras</span>
                        </div>
                    </h1>

                    {/* Animated Subtitle */}
                    <p className="hero-subtitle" style={{
                        fontSize: 'clamp(20px, 3vw, 32px)',
                        color: 'var(--accent-color)',
                        fontWeight: '500',
                        marginBottom: '30px',
                        letterSpacing: '3px',
                        textTransform: 'uppercase'
                    }}>
                        Global Sourcing & Design Excellence
                    </p>

                    {/* Description */}
                    <p style={{
                        fontSize: 'clamp(16px, 2vw, 20px)',
                        color: 'rgba(245, 241, 237, 0.9)',
                        maxWidth: '800px',
                        margin: '0 auto 50px',
                        lineHeight: '1.6',
                        fontWeight: '300'
                    }}>
                        Transform your space with 25+ years of sourcing expertise, 8 specialized studios, and access to premium materials from around the world
                    </p>

                    {/* CTA Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        marginBottom: '60px'
                    }}>
                        <Link 
                            to="/studios"
                            style={{
                                padding: '18px 45px',
                                background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                color: '#000',
                                textDecoration: 'none',
                                borderRadius: '50px',
                                fontSize: '16px',
                                fontWeight: '600',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                transition: 'all 0.3s ease',
                                border: '2px solid var(--accent-color)',
                                boxShadow: '0 10px 30px rgba(201, 169, 110, 0.3)',
                                display: 'inline-block'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-3px)';
                                e.target.style.boxShadow = '0 15px 40px rgba(201, 169, 110, 0.5)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 10px 30px rgba(201, 169, 110, 0.3)';
                            }}
                        >
                            Explore Our 8 Exclusive Studios
                        </Link>
                        
                        
                    </div>

                    {/* Stats Counter */}
                    <div style={{
                        display: 'flex',
                        gap: '60px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: '300', color: 'var(--accent-color)', marginBottom: '5px' }}>
                                25+
                            </div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.8)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                                Years Legacy
                            </div>
                        </div>
                        <div style={{ width: '1px', background: 'rgba(201, 169, 110, 0.3)' }}></div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: '300', color: 'var(--accent-color)', marginBottom: '5px' }}>
                                8
                            </div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.8)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                                Studios
                            </div>
                        </div>
                        <div style={{ width: '1px', background: 'rgba(201, 169, 110, 0.3)' }}></div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: '300', color: 'var(--accent-color)', marginBottom: '5px' }}>
                                48+
                            </div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.8)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                                Segments
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="scroll-indicator" style={{
                    position: 'absolute',
                    bottom: '40px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <span style={{ 
                        fontSize: '12px', 
                        color: 'rgba(245, 241, 237, 0.7)', 
                        letterSpacing: '2px',
                        textTransform: 'uppercase'
                    }}>
                        
                    </span>
                    <div className="scroll-line" style={{
                        width: '2px',
                        height: '60px',
                        background: 'linear-gradient(to bottom, var(--accent-color), transparent)',
                        animation: 'scrollAnimation 2s infinite'
                    }}></div>
                </div>

                {/* Add Keyframe Animation */}
                <style jsx>{`
                    @keyframes scrollAnimation {
                        0% {
                            opacity: 0;
                            transform: translateY(-20px);
                        }
                        50% {
                            opacity: 1;
                        }
                        100% {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                    }

                    @keyframes floatAnimation {
                        0%, 100% {
                            transform: translateY(0) rotate(0deg);
                        }
                        50% {
                            transform: translateY(-20px) rotate(5deg);
                        }
                    }

                    .floating-material-img {
                        animation: floatAnimation 6s ease-in-out infinite;
                    }

                    .floating-img-0 {
                        animation-delay: 0s;
                    }

                    .floating-img-1 {
                        animation-delay: 2s;
                    }

                    .floating-img-2 {
                        animation-delay: 4s;
                    }
                `}</style>
            </section>

            {/* Projects Gallery - Our 8 Design Studios - Split Screen Slider */}
            <section className="projects" id="projects">
                <div className="projects-split-container">
                    {/* Left Side - Image Slider with Overlay Content */}
                    <div className="projects-image-side">
                        {projects.map((project, index) => (
                            <div 
                                key={project.id}
                                className={`project-image-slide ${activeProject === index ? 'active' : ''}`}
                            >
                                <img src={project.image} alt={project.title} />
                                <div className="project-image-overlay">
                                    {/* Left Overlay Content */}
                                    <div className="project-overlay-content">
                                        <div className="project-logo">Hav'n Interiors</div>
                                        <div className="project-overlay-number">{String(index + 1).padStart(2, '0')}</div>
                                        <div className="project-overlay-counter">0{index + 1}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Right Side - Content with Vertical Dots Navigation */}
                    <div className="projects-content-side">
                        <div className="projects-content-wrapper">
                            {/* Content Slider */}
                            <div className="projects-slider">
                                {projects.map((project, index) => (
                                    <div 
                                        key={project.id}
                                        className={`project-slide ${activeProject === index ? 'active' : ''}`}
                                    >
                                        <div className="project-slide-header">
                                            <span className="project-slide-counter">0{index + 1}</span>
                                            <span className="project-slide-total">0{projects.length}</span>
                                        </div>
                                        <h1 className="project-slide-main-title">{project.title.split(' - ')[0]}</h1>
                                        <p className="project-slide-description">{project.description}</p>
                                        {/*  <button className="project-cta-btn">VIEW PROJECT</button>
                                  */}  </div>
                                ))}
                            </div>

                            {/* Vertical Dots Navigation (Right Side) */}
                            <div className="projects-dots-nav">
                                {projects.map((project, index) => (
                                    <button
                                        key={project.id}
                                        className={`project-dot ${activeProject === index ? 'active' : ''}`}
                                        onClick={() => setActiveProject(index)}
                                        aria-label={`Go to project ${index + 1}`}
                                    />
                                ))}
                            </div>

                            {/* Arrow Navigation (Bottom) */}
                            <div className="projects-arrows-bottom">
                                <button 
                                    className="project-arrow-btn up"
                                    onClick={() => {
                                        const prevIndex = activeProject === 0 ? projects.length - 1 : activeProject - 1;
                                        setActiveProject(prevIndex);
                                    }}
                                    aria-label="Previous project"
                                >
                                    <span>↑</span>
                                </button>
                                <button 
                                    className="project-arrow-btn down"
                                    onClick={() => {
                                        const nextIndex = activeProject === projects.length - 1 ? 0 : activeProject + 1;
                                        setActiveProject(nextIndex);
                                    }}
                                    aria-label="Next project"
                                >
                                    <span>↓</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 73% Solution Section */}
            <section className="solution-73" style={{ background: '#0F0F0F', padding: '120px 60px', position: 'relative' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <div className="responsive-grid grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}>
                        {/* Left Side - Table */}
                        <div className="table-responsive">
                            <table className="solution-table">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th>% Allocation</th>
                                        <th>Cost (₹ in Lakhs)</th>
                                        <th>Our Coverage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>1. Civil Modifications (Partition Walls, etc.)</td>
                                        <td>6%</td>
                                        <td>₹6.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr>
                                        <td>2. False Ceiling & POP Works</td>
                                        <td>8%</td>
                                        <td>₹8.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>3. Painting & Wall Finishes (Texture, Wallpapers)</td>
                                        <td>7%</td>
                                        <td>₹7.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>4. Flooring (Tiles, Wooden, Stone Inlays)</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>5. Electrical Work & Lighting</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr>
                                        <td>6. Plumbing Alteration & Sanitary Fixtures</td>
                                        <td>5%</td>
                                        <td>₹5.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr>
                                        <td>7. Modular Kitchen</td>
                                        <td>8%</td>
                                        <td>₹8.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>8. Wardrobes & Storage Units</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>9. Loose Furniture (Beds, Sofas, Dining, etc.)</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>10. Soft Furnishing (Curtains, Blinds, Rugs)</td>
                                        <td>5%</td>
                                        <td>₹5.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>11. Appliances (AC, Chimney, Hob, etc.)</td>
                                        <td>5%</td>
                                        <td>₹5.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>12. Décor, Art, Mirrors, Accessories</td>
                                        <td>4%</td>
                                        <td>₹4.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>13. Project Management & Design Fees</td>
                                        <td>6%</td>
                                        <td>₹6.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>14. Contingency / Buffer</td>
                                        <td>6%</td>
                                        <td>₹6.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr className="total-row">
                                        <td><strong>Total</strong></td>
                                        <td><strong>100%</strong></td>
                                        <td><strong>₹100.00 Lakh</strong></td>
                                        <td><strong>73% Covered by Us</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Right Side - Content */}
                        <div style={{ paddingTop: '20px' }}>
                            <h2 style={{ ...TYPOGRAPHY.h2, marginBottom: '20px', color: 'var(--accent-color)' }}>
                                The 73% Solution
                            </h2>
                            <h3 style={{ ...TYPOGRAPHY.h3, marginBottom: '30px' }}>
                                A New Standard for Project Success
                            </h3>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '30px', lineHeight: '1.8' }}>
                                We empower with a wide coverage of your interior requirements
                            </p>

                            <div style={{ background: 'rgba(201, 169, 110, 0.1)', padding: '30px', borderRadius: '12px', border: '1px solid rgba(201, 169, 110, 0.3)', marginBottom: '30px' }}>
                                <h4 style={{ fontSize: '18px', color: 'var(--accent-color)', marginBottom: '15px' }}>
                                    For a villa project of 4,500 sq.ft.<br/>
                                    (₹100 Lakh Budget),
                                </h4>
                                <p style={{ ...TYPOGRAPHY.body, lineHeight: '1.7' }}>
                                    here's how the cost allocation works. As your distributor partner, we cover <strong style={{ color: 'var(--accent-color)' }}>73% of the project requirements</strong> – giving you unmatched advantage in cross-selling and project execution.
                                </p>
                            </div>

                            {/*<div style={{ background: 'rgba(255, 77, 77, 0.1)', padding: '25px', borderRadius: '12px', border: '1px solid rgba(255, 77, 77, 0.3)' }}>
                                <p style={{ fontSize: '15px', color: 'rgba(245, 241, 237, 0.9)', lineHeight: '1.6', marginBottom: '10px' }}>
                                    <strong style={{ color: '#ff6b6b' }}>Note:</strong> Need the half house with all the percentage marking
                                </p>
                                <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', lineHeight: '1.6' }}>
                                    We provide comprehensive support across 73% of your project needs, ensuring seamless execution and maximum value.
                                </p>
                            </div>*/}
                        </div>
                    </div>
                </div>
            </section>

            {/* Global Sourcing & OEM/ODM Section */}
            <section className="sourcing-info" style={{ background: '#1a1a1a', padding: '120px 60px', position: 'relative' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <h2 style={{ ...TYPOGRAPHY.h2, textAlign: 'center', marginBottom: '30px' }}>
                        Global Sourcing & Local Excellence
                    </h2>
                    <p style={{ ...TYPOGRAPHY.body, textAlign: 'center', marginBottom: '80px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
                        We bridge the gap between international quality and local expertise, bringing you the best of both worlds.
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '50px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', padding: '50px 40px', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)' }}>
                            <div style={{ width: '60px', height: '60px', background: 'rgba(201, 169, 110, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                            </div>
                            <h3 style={{ ...TYPOGRAPHY.h4, marginBottom: '20px' }}>Global Sourcing</h3>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '25px' }}>
                                Access to premium materials and furnishings from leading manufacturers worldwide.
                            </p>
                            <img src={PLACEHOLDER_IMAGES.materials} alt="Global Sourcing" style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '12px', marginTop: '20px' }} />
                        </div>

                        <div style={{ background: 'linear-gradient(135deg, #0f3443 0%, #1a4d5c 100%)', padding: '50px 40px', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)' }}>
                            <div style={{ width: '60px', height: '60px', background: 'rgba(201, 169, 110, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                    <rect x="2" y="7" width="20" height="14" rx="2"/>
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                                    <line x1="7" y1="11" x2="7" y2="11"/>
                                    <line x1="12" y1="11" x2="12" y2="11"/>
                                    <line x1="17" y1="11" x2="17" y2="11"/>
                                </svg>
                            </div>
                            <h3 style={{ ...TYPOGRAPHY.h4, marginBottom: '20px' }}>Local OEM/ODM</h3>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '25px' }}>
                                Custom manufacturing with local artisans and manufacturers for unique, cost-effective solutions.
                            </p>
                            <img src={PLACEHOLDER_IMAGES.industrial} alt="Local Manufacturing" style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '12px', marginTop: '20px' }} />
                        </div>

                        <div style={{ background: 'linear-gradient(135deg, #2d1b2e 0%, #3d2b3e 100%)', padding: '50px 40px', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)' }}>
                            <div style={{ width: '60px', height: '60px', background: 'rgba(201, 169, 110, 0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                                </svg>
                            </div>
                            <h3 style={{ ...TYPOGRAPHY.h4, marginBottom: '20px' }}>Seamless Integration</h3>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '25px' }}>
                                Perfect harmony between international standards and local craftsmanship.
                            </p>
                            <img src={PLACEHOLDER_IMAGES.design} alt="Seamless Integration" style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '12px', marginTop: '20px' }} />
                        </div>
                    </div>

                    {/* Coverage Statistics - Enhanced with Details */}
                    <div style={{ marginTop: '80px', textAlign: 'center', padding: '60px 40px', background: 'rgba(201, 169, 110, 0.1)', borderRadius: '16px', border: '2px solid var(--accent-color)' }}>
                        <h3 style={{ ...TYPOGRAPHY.h3, marginBottom: '20px' }}>Our Coverage</h3>
                        <p style={{ fontSize: '18px', color: 'rgba(245, 241, 237, 0.8)', marginBottom: '50px', maxWidth: '800px', margin: '0 auto 50px', lineHeight: '1.7' }}>
                            We serve a diverse range of industries with comprehensive interior solutions, backed by global partnerships and unlimited creative potential.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '40px' }}>
                            <div style={{ padding: '30px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', transition: 'all 0.3s ease' }} className="coverage-card">
                                <div style={{ fontSize: '56px', fontWeight: 200, color: 'var(--accent-color)', marginBottom: '10px' }}>50+</div>
                                <div style={{ fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-color)', marginBottom: '15px', fontWeight: 600 }}>Industry Segments</div>
                                <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', lineHeight: '1.6' }}>
                                    From residential to commercial, hospitality to healthcare - we cater to specialized needs across all major industry verticals.
                                </p>
                            </div>
                            <div style={{ padding: '30px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', transition: 'all 0.3s ease' }} className="coverage-card">
                                <div style={{ fontSize: '56px', fontWeight: 200, color: 'var(--accent-color)', marginBottom: '10px' }}>4</div>
                                <div style={{ fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-color)', marginBottom: '15px', fontWeight: 600 }}>Major Categories</div>
                                <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', lineHeight: '1.6' }}>
                                    Specialized, Institutional, Commercial, and Residential - comprehensive coverage across all interior design categories.
                                </p>
                            </div>
                            <div style={{ padding: '30px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', transition: 'all 0.3s ease' }} className="coverage-card">
                                <div style={{ fontSize: '56px', fontWeight: 200, color: 'var(--accent-color)', marginBottom: '10px' }}>100+</div>
                                <div style={{ fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-color)', marginBottom: '15px', fontWeight: 600 }}>Global Partners</div>
                                <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', lineHeight: '1.6' }}>
                                    Strategic partnerships with leading international manufacturers and suppliers ensuring premium quality and competitive pricing.
                                </p>
                            </div>
                            <div style={{ padding: '30px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', transition: 'all 0.3s ease' }} className="coverage-card">
                                <div style={{ fontSize: '56px', fontWeight: 200, color: 'var(--accent-color)', marginBottom: '10px' }}>∞</div>
                                <div style={{ fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-color)', marginBottom: '15px', fontWeight: 600 }}>Design Possibilities</div>
                                <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', lineHeight: '1.6' }}>
                                    Unlimited creative potential through our 8 specialized studios, custom OEM/ODM capabilities, and global sourcing network.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section - Redesigned */}
            <section className="about" id="about" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #0F0F0F 50%, #1a1a1a 100%)', padding: '120px 5%', position: 'relative', overflow: 'hidden' }}>
                {/* Background decorative elements */}
                <div style={{ position: 'absolute', top: '10%', right: '-5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(201, 169, 110, 0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}></div>
                <div style={{ position: 'absolute', bottom: '10%', left: '-5%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(201, 169, 110, 0.08) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}></div>

                <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    {/* Hero Introduction */}
                    <div style={{ textAlign: 'center', marginBottom: '80px' }}>
                        <div style={{ display: 'inline-block', padding: '10px 30px', background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '50px', marginBottom: '30px' }}>
                            <span style={{ fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-color)', fontWeight: 500 }}>About HAV'N</span>
                        </div>
                        <h2 style={{ ...TYPOGRAPHY.h1, marginBottom: '30px', background: 'linear-gradient(135deg, #F5F1ED 0%, #C9A96E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                            Where Design Meets<br/>Excellence
                        </h2>
                        <p style={{ ...TYPOGRAPHY.bodyLarge, maxWidth: '900px', margin: '0 auto', color: 'rgba(245, 241, 237, 0.8)' }}>
                            Chennai's premier design destination backed by <span style={{ color: 'var(--accent-color)', fontWeight: 500 }}>45 years of R B Shah Group expertise</span>
                        </p>
                    </div>

                    {/* Main Content Grid */}
                    <div className="responsive-grid grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', marginBottom: '80px' }}>
                        {/* Left Side - Image & Stats */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                            <div style={{ position: 'relative', borderRadius: '24px', overflow: 'hidden', height: '400px', border: '2px solid var(--accent-color)', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)' }}>
                                <img src={PLACEHOLDER_IMAGES.design} alt="HAV'N Interiors" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '30px', background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.9))' }}>
                                    <h3 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '10px' }}>8 Design Studios</h3>
                                    <p style={{ ...TYPOGRAPHY.body, margin: 0 }}>One Unified Experience Centre</p>
                                </div>
                            </div>
                            
                            {/* Quick Stats */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div style={{ background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.15) 0%, rgba(201, 169, 110, 0.05) 100%)', padding: '30px', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)', textAlign: 'center' }}>
                                    <div style={{ fontSize: '48px', fontWeight: 200, color: 'var(--accent-color)', marginBottom: '10px' }}>
                                        <CounterAnimation end={45} suffix="+" duration={2500} />
                                    </div>
                                    <div style={{ fontSize: '14px', letterSpacing: '1px', color: 'rgba(245, 241, 237, 0.7)' }}>Years Experience</div>
                                </div>
                                <div style={{ background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.15) 0%, rgba(201, 169, 110, 0.05) 100%)', padding: '30px', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)', textAlign: 'center' }}>
                                    <div style={{ fontSize: '48px', fontWeight: 200, color: 'var(--accent-color)', marginBottom: '10px' }}>
                                        <CounterAnimation end={73} suffix="%" duration={2500} />
                                    </div>
                                    <div style={{ fontSize: '14px', letterSpacing: '1px', color: 'rgba(245, 241, 237, 0.7)' }}>Complete Solution</div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Content */}
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '30px' }}>
                            <div>
                                <h3 style={{ ...TYPOGRAPHY.h3, color: 'var(--accent-color)', marginBottom: '20px' }}>Who We Are</h3>
                                <p style={{ ...TYPOGRAPHY.body, marginBottom: '20px' }}>
                                    Welcome to <strong style={{ color: 'var(--accent-color)' }}>HAV'N Interiors & Global Sourcing</strong>, where innovation meets tradition. Located in Periyamet, Chennai, our facility features eight distinct Interior product Experience Centres under one roof.
                                </p>
                                <p style={{ ...TYPOGRAPHY.body }}>
                                    We partner with homeowners, architects, and interior firms, providing comprehensive services including our signature <strong style={{ color: 'var(--accent-color)' }}>73% Solution</strong> and <strong style={{ color: 'var(--accent-color)' }}>Café Connections</strong> - the city's first exclusive architects' café with a complete material library.
                                </p>
                            </div>

                            {/* Key Features */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {[
                                    { icon: 'palette', title: 'Design Excellence', desc: 'Curated selection of premium materials' },
                                    { icon: 'globe', title: 'Global Sourcing', desc: 'International quality, local expertise' },
                                    { icon: 'users', title: 'Café Connections', desc: 'Collaborative workspace for designers' }
                                ].map((feature, idx) => (
                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px', background: 'rgba(201, 169, 110, 0.05)', borderRadius: '12px', border: '1px solid rgba(201, 169, 110, 0.15)', transition: 'all 0.3s ease' }} className="feature-hover">
                                        <div style={{ minWidth: '50px', textAlign: 'center' }}>
                                            {feature.icon === 'palette' && (
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                    <circle cx="13.5" cy="6.5" r=".5"/>
                                                    <circle cx="17.5" cy="10.5" r=".5"/>
                                                    <circle cx="8.5" cy="7.5" r=".5"/>
                                                    <circle cx="6.5" cy="12.5" r=".5"/>
                                                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
                                                </svg>
                                            )}
                                            {feature.icon === 'globe' && (
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"/>
                                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                                </svg>
                                            )}
                                            {feature.icon === 'users' && (
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                    <circle cx="9" cy="7" r="4"/>
                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                </svg>
                                            )}
                                        </div>
                                        <div>
                                            <h4 style={{ ...TYPOGRAPHY.h4, fontSize: '18px', marginBottom: '5px', color: 'var(--text-light)' }}>{feature.title}</h4>
                                            <p style={{ ...TYPOGRAPHY.body, fontSize: '14px', margin: 0, color: 'rgba(245, 241, 237, 0.7)' }}>{feature.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Vision & Mission Cards */}
                    <div className="responsive-grid grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                        {/* Vision Card */}
                        <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderRadius: '24px', border: '2px solid rgba(201, 169, 110, 0.3)', position: 'relative', overflow: 'hidden' }}>
                            {/* Vision Image */}
                            <div style={{ position: 'relative', height: '250px', overflow: 'hidden' }}>
                                <img src={PLACEHOLDER_IMAGES.cafe} alt="Vision - Café Connections" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 0%, rgba(26, 26, 46, 0.9) 100%)' }}></div>
                                <div style={{ position: 'absolute', top: '20px', left: '20px' }}>
                                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                </div>
                            </div>
                            
                            {/* Vision Content */}
                            <div style={{ padding: '40px' }}>
                                <h3 style={{ ...TYPOGRAPHY.h3, color: 'var(--accent-color)', marginBottom: '25px' }}>Our Vision</h3>
                                <p style={{ ...TYPOGRAPHY.body, marginBottom: '20px' }}>
                                    To pioneer India's first free co-working café for interior designers and architects, where <strong style={{ color: 'var(--accent-color)' }}>creativity, collaboration, and commerce converge</strong>.
                                </p>
                                <div style={{ padding: '20px', background: 'rgba(201, 169, 110, 0.1)', borderRadius: '12px', borderLeft: '4px solid var(--accent-color)' }}>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0, fontStyle: 'italic' }}>
                                        Fostering a seamlessly connected global and local ecosystem of designers, dealers, distributors, manufacturers, and clients.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Mission Card */}
                        <div style={{ background: 'linear-gradient(135deg, #0f3443 0%, #1a4d5c 100%)', borderRadius: '24px', border: '2px solid rgba(201, 169, 110, 0.3)', position: 'relative', overflow: 'hidden' }}>
                            {/* Mission Image */}
                            <div style={{ position: 'relative', height: '250px', overflow: 'hidden' }}>
                                <img src={PLACEHOLDER_IMAGES.team} alt="Mission - Empowering Community" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 0%, rgba(15, 52, 67, 0.9) 100%)' }}></div>
                                <div style={{ position: 'absolute', top: '20px', left: '20px' }}>
                                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                </div>
                            </div>
                            
                            {/* Mission Content */}
                            <div style={{ padding: '40px' }}>
                                <h3 style={{ ...TYPOGRAPHY.h3, color: 'var(--accent-color)', marginBottom: '25px' }}>Our Mission</h3>
                                <p style={{ ...TYPOGRAPHY.body, marginBottom: '20px' }}>
                                    To <strong style={{ color: 'var(--accent-color)' }}>empower India's interior design community</strong> by providing a trusted platform that fosters creativity, collaboration, and commerce.
                                </p>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {['Crafting inspiring spaces', 'Global resources & collaborations', 'Building lasting associations'].map((item, idx) => (
                                        <li key={idx} style={{ ...TYPOGRAPHY.body, fontSize: '15px', padding: '12px 0', borderBottom: idx < 2 ? '1px solid rgba(201, 169, 110, 0.2)' : 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ color: 'var(--accent-color)', fontSize: '20px' }}>✓</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* HAV'N Experience Center - Unified Tabbed Section */}
                    <div style={{ marginTop: '80px', padding: '60px 40px', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderRadius: '24px', border: '2px solid var(--accent-color)' }}>
                        {/* Header */}
                        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                            <div style={{ display: 'inline-block', padding: '10px 30px', background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '50px', marginBottom: '20px' }}>
                                <span style={{ fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-color)', fontWeight: 500 }}>Experience Center</span>
                            </div>
                            <h3 style={{ ...TYPOGRAPHY.h2, color: 'var(--accent-color)', marginBottom: '15px' }}>The HAV'N Experience</h3>
                            <p style={{ ...TYPOGRAPHY.body, maxWidth: '800px', margin: '0 auto', fontStyle: 'italic', color: 'rgba(245, 241, 237, 0.8)' }}>
                                Discover our three pillars of excellence that make HAV'N Interiors unique
                            </p>
                        </div>

                        {/* Tab Navigation */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '50px', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => setActiveExperienceTab('cafe')}
                                style={{
                                    padding: '18px 40px',
                                    background: activeExperienceTab === 'cafe' ? 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)' : 'rgba(201, 169, 110, 0.1)',
                                    color: activeExperienceTab === 'cafe' ? '#000' : 'var(--accent-color)',
                                    border: `2px solid ${activeExperienceTab === 'cafe' ? 'var(--accent-color)' : 'rgba(201, 169, 110, 0.3)'}`,
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                                onMouseEnter={(e) => {
                                    if (activeExperienceTab !== 'cafe') {
                                        e.target.style.background = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.borderColor = 'var(--accent-color)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeExperienceTab !== 'cafe') {
                                        e.target.style.background = 'rgba(201, 169, 110, 0.1)';
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                    }
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                                    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                                    <line x1="6" y1="1" x2="6" y2="4"/>
                                    <line x1="10" y1="1" x2="10" y2="4"/>
                                    <line x1="14" y1="1" x2="14" y2="4"/>
                                </svg>
                                Café Connections
                            </button>
                            
                            <button
                                onClick={() => setActiveExperienceTab('design')}
                                style={{
                                    padding: '18px 40px',
                                    background: activeExperienceTab === 'design' ? 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)' : 'rgba(201, 169, 110, 0.1)',
                                    color: activeExperienceTab === 'design' ? '#000' : 'var(--accent-color)',
                                    border: `2px solid ${activeExperienceTab === 'design' ? 'var(--accent-color)' : 'rgba(201, 169, 110, 0.3)'}`,
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                                onMouseEnter={(e) => {
                                    if (activeExperienceTab !== 'design') {
                                        e.target.style.background = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.borderColor = 'var(--accent-color)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeExperienceTab !== 'design') {
                                        e.target.style.background = 'rgba(201, 169, 110, 0.1)';
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                    }
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="13.5" cy="6.5" r=".5"/>
                                    <circle cx="17.5" cy="10.5" r=".5"/>
                                    <circle cx="8.5" cy="7.5" r=".5"/>
                                    <circle cx="6.5" cy="12.5" r=".5"/>
                                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
                                </svg>
                                Design Excellence
                            </button>
                            
                            <button
                                onClick={() => setActiveExperienceTab('global')}
                                style={{
                                    padding: '18px 40px',
                                    background: activeExperienceTab === 'global' ? 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)' : 'rgba(201, 169, 110, 0.1)',
                                    color: activeExperienceTab === 'global' ? '#000' : 'var(--accent-color)',
                                    border: `2px solid ${activeExperienceTab === 'global' ? 'var(--accent-color)' : 'rgba(201, 169, 110, 0.3)'}`,
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                                onMouseEnter={(e) => {
                                    if (activeExperienceTab !== 'global') {
                                        e.target.style.background = 'rgba(201, 169, 110, 0.2)';
                                        e.target.style.borderColor = 'var(--accent-color)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeExperienceTab !== 'global') {
                                        e.target.style.background = 'rgba(201, 169, 110, 0.1)';
                                        e.target.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                    }
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                                Global Sourcing
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div style={{ minHeight: '400px' }}>
                            {/* Café Connections Content */}
                            {activeExperienceTab === 'cafe' && (
                                <div>
                        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                    <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                                    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                                    <line x1="6" y1="1" x2="6" y2="4"/>
                                    <line x1="10" y1="1" x2="10" y2="4"/>
                                    <line x1="14" y1="1" x2="14" y2="4"/>
                                </svg>
                            </div>
                            <h3 style={{ ...TYPOGRAPHY.h2, color: 'var(--accent-color)', marginBottom: '15px' }}>Café Connections</h3>
                            <p style={{ fontSize: '20px', color: 'var(--text-light)', fontWeight: 500, marginBottom: '10px' }}>Studio Connect Café</p>
                            <p style={{ ...TYPOGRAPHY.body, maxWidth: '800px', margin: '0 auto', fontStyle: 'italic' }}>
                                Where Ideas Brew, Designs Emerge, and Communities Connect
                            </p>
                        </div>

                        {/* Café Video Showcase */}
                        <div style={{ marginBottom: '50px', borderRadius: '20px', overflow: 'hidden', border: '2px solid var(--accent-color)', boxShadow: '0 10px 40px rgba(201, 169, 110, 0.3)' }}>
                            <video 
                                autoPlay 
                                loop 
                                muted 
                                playsInline
                                style={{ 
                                    width: '100%', 
                                    height: 'auto', 
                                    display: 'block',
                                    maxHeight: '600px',
                                    objectFit: 'cover'
                                }}
                            >
                                <source src={`${process.env.PUBLIC_URL}/images/awards/cafe.mp4`} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>

                        {/* What is Café Connections */}
                        <div style={{ marginBottom: '40px', padding: '40px', background: 'rgba(201, 169, 110, 0.05)', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)' }}>
                            <h4 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '20px' }}>What is Café Connections?</h4>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '25px' }}>
                                Café Connections is more than a café — it is a platform for networking, learning, and creative exchange. 
                                The city's first exclusive architects' café with a complete material library, offering a professional hub where design takes shape.
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                                            <line x1="12" y1="19" x2="12" y2="23"/>
                                            <line x1="8" y1="23" x2="16" y2="23"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        A stage for seminars, workshops, and design talks
                                    </p>
                                </div>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <circle cx="13.5" cy="6.5" r=".5"/>
                                            <circle cx="17.5" cy="10.5" r=".5"/>
                                            <circle cx="8.5" cy="7.5" r=".5"/>
                                            <circle cx="6.5" cy="12.5" r=".5"/>
                                            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        An experience studio to explore, touch, and interact with diverse material solutions
                                    </p>
                                </div>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                            <circle cx="9" cy="7" r="4"/>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        A meeting point for architects, interior designers, institutes, and students to collaborate
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Explore Button */}
                        <div style={{ textAlign: 'center', marginTop: '50px' }}>
                            <button
                                onClick={() => navigate('/cafe-connections')}
                                style={{
                                    padding: '16px 40px',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#0A0A0A',
                                    background: 'var(--accent-color)',
                                    border: '2px solid var(--accent-color)',
                                    borderRadius: '50px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    boxShadow: '0 4px 15px rgba(201, 169, 110, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'transparent';
                                    e.target.style.color = 'var(--accent-color)';
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(201, 169, 110, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'var(--accent-color)';
                                    e.target.style.color = '#0A0A0A';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 15px rgba(201, 169, 110, 0.3)';
                                }}
                            >
                                Explore our cafe
                            </button>
                        </div>
                                </div>
                            )}

                            {/* Design Excellence Content */}
                            {activeExperienceTab === 'design' && (
                                <div>
                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <p style={{ fontSize: '20px', color: 'var(--text-light)', fontWeight: 500, marginBottom: '10px' }}>Curated Premium Materials</p>
                            <p style={{ ...TYPOGRAPHY.body, maxWidth: '800px', margin: '0 auto', fontStyle: 'italic' }}>
                                Where Quality Meets Creativity, and Every Material Tells a Story
                            </p>
                        </div>

                        {/* What is Design Excellence */}
                        <div style={{ marginBottom: '40px', padding: '40px', background: 'rgba(201, 169, 110, 0.05)', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)' }}>
                            <h4 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '20px' }}>What is Design Excellence?</h4>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '25px' }}>
                                Design Excellence is our commitment to providing only the finest, most innovative materials and finishes for your interior projects. 
                                We curate a premium selection from leading global and local manufacturers, ensuring every product meets our rigorous standards of quality, aesthetics, and durability.
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <polyline points="20 6 9 17 4 12"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        Handpicked materials from premium brands worldwide
                                    </p>
                                </div>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        Quality-tested products that stand the test of time
                                    </p>
                                </div>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                                            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                                            <line x1="12" y1="22.08" x2="12" y2="12"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        Comprehensive material library with live samples
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Our Material Categories */}
                        <div style={{ marginBottom: '40px' }}>
                            <h4 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '30px', textAlign: 'center' }}>Our Material Categories</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                                {[
                                    { icon: 'wood', title: 'Premium Woods & Veneers', desc: 'Teak, engineered woods, laminates, and decorative sheets' },
                                    { icon: 'stone', title: 'Natural Stones', desc: 'Marble, granite, and premium stone cladding solutions' },
                                    { icon: 'fabric', title: 'Textiles & Fabrics', desc: 'Carpets, curtains, upholstery, and soft furnishings' },
                                    { icon: 'ceiling', title: 'Ceiling Solutions', desc: 'Gypsum boards, soffits, and aluminium profiles' },
                                    { icon: 'floor', title: 'Flooring Options', desc: 'Wooden flooring, tiles, and innovative surface solutions' },
                                    { icon: 'hardware', title: 'Hardware & Fixtures', desc: 'Door hardware, bathroom fittings, and kitchen accessories' }
                                ].map((category, idx) => (
                                    <div key={idx} style={{ padding: '25px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', border: '1px solid rgba(201, 169, 110, 0.2)', transition: 'all 0.3s ease' }} className="design-category-card">
                                        <div style={{ marginBottom: '15px' }}>
                                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                                <polyline points="21 15 16 10 5 21"/>
                                            </svg>
                                        </div>
                                        <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '18px', color: 'var(--accent-color)', marginBottom: '10px' }}>{category.title}</h5>
                                        <p style={{ ...TYPOGRAPHY.body, fontSize: '14px', margin: 0, lineHeight: '1.6' }}>{category.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Why Choose Our Materials */}
                        <div style={{ padding: '30px', background: 'rgba(201, 169, 110, 0.1)', borderRadius: '16px', border: '1px solid var(--accent-color)' }}>
                            <h4 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '20px', textAlign: 'center' }}>Why Choose Our Materials?</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', textAlign: 'center' }}>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                        </svg>
                                    </div>
                                    <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>Cost-Effective</h5>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '14px' }}>Competitive pricing without compromising on quality</p>
                                </div>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <polyline points="12 6 12 12 16 14"/>
                                        </svg>
                                    </div>
                                    <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>Timely Delivery</h5>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '14px' }}>Reliable supply chain ensuring on-time project completion</p>
                                </div>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                            <polyline points="14 2 14 8 20 8"/>
                                            <line x1="16" y1="13" x2="8" y2="13"/>
                                            <line x1="16" y1="17" x2="8" y2="17"/>
                                            <polyline points="10 9 9 9 8 9"/>
                                        </svg>
                                    </div>
                                    <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>Documentation</h5>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '14px' }}>Complete technical specifications and warranty support</p>
                                </div>
                            </div>
                        </div>
                                </div>
                            )}

                            {/* Global Sourcing Content */}
                            {activeExperienceTab === 'global' && (
                                <div>
                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <p style={{ fontSize: '20px', color: 'var(--text-light)', fontWeight: 500, marginBottom: '10px' }}>International Quality, Local Expertise</p>
                            <p style={{ ...TYPOGRAPHY.body, maxWidth: '800px', margin: '0 auto', fontStyle: 'italic' }}>
                                Connecting You to the World's Finest Interior Solutions
                            </p>
                        </div>

                        {/* What is Global Sourcing */}
                        <div style={{ marginBottom: '40px', padding: '40px', background: 'rgba(201, 169, 110, 0.05)', borderRadius: '16px', border: '1px solid rgba(201, 169, 110, 0.2)' }}>
                            <h4 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '20px' }}>What is Global Sourcing?</h4>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '25px' }}>
                                Backed by the R B Shah Group's 45 years of international trade experience, our Global Sourcing division connects you to premium manufacturers, 
                                suppliers, and artisans from around the world. We bring international design trends and quality products to the Indian market while maintaining 
                                competitive pricing and reliable delivery.
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                            <circle cx="12" cy="10" r="3"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        Direct partnerships with manufacturers in Europe, Asia, and Americas
                                    </p>
                                </div>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                                            <line x1="7" y1="7" x2="7.01" y2="7"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        Exclusive access to limited-edition and custom-manufactured products
                                    </p>
                                </div>
                                <div style={{ padding: '20px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', borderLeft: '3px solid var(--accent-color)' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                            <circle cx="8.5" cy="7" r="4"/>
                                            <polyline points="17 11 19 13 23 9"/>
                                        </svg>
                                    </div>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '15px', margin: 0 }}>
                                        Quality assurance and compliance with international standards
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Our Global Network */}
                        <div style={{ marginBottom: '40px' }}>
                            <h4 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '30px', textAlign: 'center' }}>Our Global Network</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                                {[
                                    { region: 'Europe', countries: 'Italy, Germany, Spain', speciality: 'Premium furniture, lighting, and fixtures' },
                                    { region: 'Asia Pacific', countries: 'China, Vietnam, Malaysia', speciality: 'Manufacturing excellence and innovation' },
                                    { region: 'Middle East', countries: 'UAE, Turkey', speciality: 'Luxury finishes and decorative elements' },
                                    { region: 'Americas', countries: 'USA, Brazil', speciality: 'Contemporary designs and technology' }
                                ].map((network, idx) => (
                                    <div key={idx} style={{ padding: '25px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', border: '1px solid rgba(201, 169, 110, 0.2)', transition: 'all 0.3s ease' }} className="global-network-card">
                                        <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>{network.region}</h5>
                                        <p style={{ ...TYPOGRAPHY.body, fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', marginBottom: '10px' }}>{network.countries}</p>
                                        <p style={{ ...TYPOGRAPHY.body, fontSize: '14px', margin: 0, lineHeight: '1.6' }}>{network.speciality}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Our Services */}
                        <div style={{ padding: '30px', background: 'rgba(201, 169, 110, 0.1)', borderRadius: '16px', border: '1px solid var(--accent-color)' }}>
                            <h4 style={{ ...TYPOGRAPHY.h4, color: 'var(--accent-color)', marginBottom: '20px', textAlign: 'center' }}>What We Offer</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', textAlign: 'center' }}>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                                        </svg>
                                    </div>
                                    <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>Product Sourcing</h5>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '14px' }}>Finding the perfect products for your specific needs</p>
                                </div>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                                        </svg>
                                    </div>
                                    <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>Custom Manufacturing</h5>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '14px' }}>OEM/ODM solutions tailored to your requirements</p>
                                </div>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <rect x="1" y="3" width="15" height="13"/>
                                            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                                            <circle cx="5.5" cy="18.5" r="2.5"/>
                                            <circle cx="18.5" cy="18.5" r="2.5"/>
                                        </svg>
                                    </div>
                                    <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>Logistics Support</h5>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '14px' }}>End-to-end shipping and customs clearance</p>
                                </div>
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                                        </svg>
                                    </div>
                                    <h5 style={{ ...TYPOGRAPHY.h4, fontSize: '20px', color: 'var(--accent-color)', marginBottom: '10px' }}>Quality Control</h5>
                                    <p style={{ ...TYPOGRAPHY.body, fontSize: '14px' }}>Rigorous inspection before shipment</p>
                                </div>
                            </div>
                        </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CTA */}
                    <div style={{ textAlign: 'center', marginTop: '80px', padding: '60px', background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.1) 0%, rgba(201, 169, 110, 0.05) 100%)', borderRadius: '24px', border: '2px solid var(--accent-color)' }}>
                        <h3 style={{ ...TYPOGRAPHY.h3, marginBottom: '20px' }}>Ready to Transform Your Space?</h3>
                        <p style={{ ...TYPOGRAPHY.bodyLarge, marginBottom: '40px', maxWidth: '700px', margin: '0 auto 40px' }}>
                            Explore retail and franchise opportunities with us. HAV'N is where design, quality, and value seamlessly unfold.
                        </p>
                        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="/about" style={{ display: 'inline-block', padding: '18px 45px', background: 'var(--accent-color)', color: '#000', textDecoration: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase', transition: 'all 0.3s ease', border: '2px solid var(--accent-color)' }} className="cta-btn-primary">
                                Learn More
                            </Link>
                            <Link to="/contact" style={{ display: 'inline-block', padding: '18px 45px', background: 'transparent', color: 'var(--accent-color)', textDecoration: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase', transition: 'all 0.3s ease', border: '2px solid var(--accent-color)' }} className="cta-btn-secondary">
                                Get in Touch
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Add hover effect styles */}
                <style jsx>{`
                    .feature-hover:hover {
                        background: rgba(201, 169, 110, 0.15) !important;
                        border-color: var(--accent-color) !important;
                        transform: translateX(10px);
                    }
                    .design-category-card:hover,
                    .global-network-card:hover {
                        transform: translateY(-5px);
                        border-color: var(--accent-color) !important;
                        box-shadow: 0 10px 30px rgba(201, 169, 110, 0.3);
                    }
                `}</style>
            </section>

            {/* Segments Section - Redesigned */}
            <section className="segments" id="segments" style={{ background: '#0F0F0F', padding: '120px 5%', position: 'relative', overflow: 'hidden' }}>
                {/* Background decoration */}
                <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(circle, rgba(201, 169, 110, 0.05) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}></div>
                
                <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    {/* Section Header */}
                    <div style={{ textAlign: 'center', marginBottom: '80px' }}>
                        <div style={{ display: 'inline-block', padding: '10px 30px', background: 'rgba(201, 169, 110, 0.1)', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '50px', marginBottom: '30px' }}>
                            <span style={{ fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--accent-color)', fontWeight: 500 }}>Our Expertise</span>
                        </div>
                        <h2 style={{ ...TYPOGRAPHY.h1, marginBottom: '30px' }}>
                            Industry Segments<br/>
                            <span style={{ color: 'var(--accent-color)' }}>We Serve</span>
                        </h2>
                        <p style={{ ...TYPOGRAPHY.bodyLarge, maxWidth: '800px', margin: '0 auto', color: 'rgba(245, 241, 237, 0.7)' }}>
                            From residential homes to commercial spaces, we bring design excellence to every segment
                        </p>
                    </div>

                    {/* Segments Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '30px' }}>
                        {segments.map((segment, index) => {
                            const segmentImages = [
                                PLACEHOLDER_IMAGES.industrial,
                                PLACEHOLDER_IMAGES.healthcare,
                                PLACEHOLDER_IMAGES.commercial,
                                PLACEHOLDER_IMAGES.residential
                            ];
                            
                            const gradients = [
                                'linear-gradient(135deg, #0f3443 0%, #1a4d5c 100%)',
                                'linear-gradient(135deg, #2d1b2e 0%, #3d2b3e 100%)',
                                'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
                            ];

                            return (
                                <div 
                                    key={segment.number} 
                                    onClick={() => handleSegmentClick(segment.number)}
                                    style={{ 
                                        background: gradients[index % 4],
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                        border: '2px solid rgba(201, 169, 110, 0.2)',
                                        transition: 'all 0.4s ease',
                                        position: 'relative',
                                        cursor: 'pointer'
                                    }} 
                                    className="segment-card-hover"
                                >
                                    {/* Image Header */}
                                    <div style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
                                        <img src={segmentImages[index % 4]} alt={segment.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.8) 100%)' }}></div>
                                        
                                        {/* Number Badge */}
                                        <div style={{ 
                                            position: 'absolute', 
                                            top: '20px', 
                                            right: '20px',
                                            width: '60px',
                                            height: '60px',
                                            background: 'var(--accent-color)',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '24px',
                                            fontWeight: 600,
                                            color: '#000',
                                            boxShadow: '0 4px 15px rgba(201, 169, 110, 0.5)'
                                        }}>
                                            {segment.number}
                                        </div>

                                        {/* Icon */}
                                        <div style={{ position: 'absolute', bottom: '20px', left: '20px' }}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
                                                <path d="M9 22v-4h6v4"/>
                                                <path d="M8 6h.01"/>
                                                <path d="M16 6h.01"/>
                                                <path d="M12 6h.01"/>
                                                <path d="M12 10h.01"/>
                                                <path d="M12 14h.01"/>
                                                <path d="M16 10h.01"/>
                                                <path d="M16 14h.01"/>
                                                <path d="M8 10h.01"/>
                                                <path d="M8 14h.01"/>
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div style={{ padding: '30px' }}>
                                        <h3 style={{ ...TYPOGRAPHY.h3, fontSize: '24px', marginBottom: '15px', color: 'var(--text-light)' }}>
                                            {segment.title}
                                        </h3>
                                        <p style={{ ...TYPOGRAPHY.body, fontSize: '14px', marginBottom: '20px', color: 'rgba(245, 241, 237, 0.8)', lineHeight: 1.6 }}>
                                            {segment.description}
                                        </p>

                                        {/* Count Badge */}
                                        <div style={{ 
                                            display: 'inline-block',
                                            padding: '8px 20px',
                                            background: 'rgba(201, 169, 110, 0.2)',
                                            borderRadius: '20px',
                                            border: '1px solid var(--accent-color)',
                                            marginBottom: '20px'
                                        }}>
                                            <span style={{ fontSize: '14px', color: 'var(--accent-color)', fontWeight: 500 }}>
                                                {segment.count}
                                            </span>
                                        </div>

                                        {/* Items Preview */}
                                        <div style={{ 
                                            background: 'rgba(0, 0, 0, 0.3)',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(201, 169, 110, 0.1)'
                                        }}>
                                            <div style={{ fontSize: '12px', color: 'var(--accent-color)', marginBottom: '10px', fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase' }}>
                                                Key Specializations
                                            </div>
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                {segment.items.slice(0, 4).map((item, idx) => (
                                                    <li key={idx} style={{ 
                                                        fontSize: '13px',
                                                        color: 'rgba(245, 241, 237, 0.9)',
                                                        padding: '8px 0',
                                                        borderBottom: idx < 3 ? '1px solid rgba(201, 169, 110, 0.1)' : 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px'
                                                    }}>
                                                        <span style={{ color: 'var(--accent-color)', fontSize: '16px' }}>→</span>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                            {segment.items.length > 4 && (
                                                <div style={{ 
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    background: 'rgba(201, 169, 110, 0.1)',
                                                    borderRadius: '8px',
                                                    textAlign: 'center',
                                                    fontSize: '12px',
                                                    color: 'var(--accent-color)'
                                                }}>
                                                    +{segment.items.length - 4} more specializations
                                                </div>
                                            )}
                                        </div>

                                        {/* View More Link */}
                                        <Link to="/segments" style={{
                                            display: 'block',
                                            marginTop: '20px',
                                            padding: '12px 0',
                                            textAlign: 'center',
                                            background: 'transparent',
                                            border: '2px solid var(--accent-color)',
                                            borderRadius: '10px',
                                            color: 'var(--accent-color)',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            transition: 'all 0.3s ease'
                                        }} className="segment-view-more">
                                            View Details
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* CTA Button */}
                    <div style={{ textAlign: 'center', marginTop: '80px' }}>
                        <Link to="/segments" style={{
                            display: 'inline-block',
                            padding: '20px 50px',
                            background: 'var(--accent-color)',
                            color: '#000',
                            textDecoration: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            transition: 'all 0.3s ease',
                            border: '2px solid var(--accent-color)',
                            boxShadow: '0 10px 30px rgba(201, 169, 110, 0.3)'
                        }} className="cta-btn-primary">
                            Explore All Segments
                        </Link>
                    </div>
                </div>

                {/* Hover Styles */}
                <style jsx>{`
                    .segment-card-hover:hover {
                        transform: translateY(-10px);
                        border-color: var(--accent-color) !important;
                        box-shadow: 0 20px 50px rgba(201, 169, 110, 0.3);
                    }
                    
                    .segment-view-more:hover {
                        background: var(--accent-color) !important;
                        color: #000 !important;
                    }
                `}</style>
            </section>

            {/* Studio Selector Circle */}
            <section className="studio-selector" id="studios" style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${PLACEHOLDER_IMAGES.design})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}>
                <h2 className="studio-selector-title">Studios</h2>
                <p className="studio-selector-subtitle">Discover 8 Unique Design Studios</p>
                
                <div className="studios-container">
                    {/* Connecting Arrows */}
                    <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
                        <defs>
                            <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
                                <polygon points="0 0, 10 3, 0 6" fill="#C9A96E" />
                            </marker>
                        </defs>
                        
                        {/* Arrows from center to each studio */}
                        <line x1="50%" y1="36%" x2="50%" y2="23%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                        <line x1="59%" y1="40%" x2="72%" y2="24%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                        <line x1="64%" y1="50%" x2="77%" y2="50%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                        <line x1="59%" y1="60%" x2="72%" y2="76%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                        <line x1="50%" y1="64%" x2="50%" y2="77%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                        <line x1="41%" y1="60%" x2="28%" y2="76%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                        <line x1="36%" y1="50%" x2="23%" y2="50%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                        <line x1="41%" y1="40%" x2="28%" y2="24%" stroke="#C9A96E" strokeWidth="2" markerEnd="url(#arrowhead)" opacity="0.6" />
                    </svg>

                    {/* Center Circle */}
                    <div className="studio-circle center">
                        <span>Hav'n</span>
                    </div>

                    {/* Surrounding Studios - Updated Names */}
                    <div className="studio-circle top" onClick={() => handleStudioClick('lumi')}>
                        <span>LUMI<br/>CEILING</span>
                    </div>

                    <div className="studio-circle top-right" onClick={() => handleStudioClick('craftcove')}>
                        <span>CRAFT<br/>COVE</span>
                    </div>

                    <div className="studio-circle right" onClick={() => handleStudioClick('thread')}>
                        <span>Thread</span>
                    </div>

                    <div className="studio-circle bottom-right" onClick={() => handleStudioClick('artistry')}>
                        <span>Artistry<br/>Room</span>
                    </div>

                    <div className="studio-circle bottom" onClick={() => handleStudioClick('globalnest')}>
                        <span>Global<br/>Nest</span>
                    </div>

                    <div className="studio-circle bottom-left" onClick={() => handleStudioClick('surface')}>
                        <span>Surface<br/>Style</span>
                    </div>

                    <div className="studio-circle left" onClick={() => handleStudioClick('locknkey')}>
                        <span>LockN<br/>Key</span>
                    </div>

                    <div className="studio-circle top-left" onClick={() => handleStudioClick('shape')}>
                        <span>Shape<br/>Studio</span>
                    </div>
                </div>
            </section>

            {/* Budget Calculator Section */}
            <section className="budget-calculator-section" style={{ background: 'linear-gradient(to bottom, #0A0A0A, #000000)', padding: '120px 60px', position: 'relative' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    {/* Section Header */}
                    <div style={{ textAlign: 'center', marginBottom: '80px' }}>
                        <h2 style={{ ...TYPOGRAPHY.h2, marginBottom: '20px', background: 'linear-gradient(135deg, var(--accent-color), #d4a574)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            Value Picks
                        </h2>
                        <p style={{ fontSize: '20px', color: 'rgba(245, 241, 237, 0.7)', maxWidth: '800px', margin: '0 auto', lineHeight: '1.6' }}>
                            Plan your interior budget with our smart calculator. Get a detailed breakdown of costs across all categories.
                        </p>
                    </div>

                    {/* Calculator Grid */}
                    <div className="responsive-grid grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', marginBottom: '80px' }}>
                        {/* Left Side - Input Form */}
                        <div className="calculator-input-panel" style={{ 
                            background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.8), rgba(10, 10, 10, 0.9))',
                            border: '1px solid rgba(201, 169, 110, 0.2)',
                            borderRadius: '20px',
                            padding: '40px',
                            backdropFilter: 'blur(10px)'
                        }}>
                            <h3 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-color)', marginBottom: '40px' }}>
                                Budget Calculator
                            </h3>

                            {/* House Type Selection */}
                            <div style={{ marginBottom: '40px' }}>
                                <label style={{ display: 'block', color: 'rgba(245, 241, 237, 0.9)', fontSize: '14px', fontWeight: '500', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Select House Type
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                                    {houseTypes.map(houseType => (
                                        <button
                                            key={houseType.id}
                                            onClick={() => handleHouseTypeSelect(houseType)}
                                            style={{
                                                padding: '20px',
                                                borderRadius: '12px',
                                                border: selectedHouseType === houseType.id 
                                                    ? '2px solid var(--accent-color)' 
                                                    : '2px solid rgba(100, 100, 100, 0.3)',
                                                background: selectedHouseType === houseType.id 
                                                    ? 'rgba(201, 169, 110, 0.15)' 
                                                    : 'rgba(20, 20, 20, 0.5)',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                textAlign: 'left'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedHouseType !== houseType.id) {
                                                    e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.5)';
                                                    e.currentTarget.style.transform = 'translateX(5px)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedHouseType !== houseType.id) {
                                                    e.currentTarget.style.borderColor = 'rgba(100, 100, 100, 0.3)';
                                                    e.currentTarget.style.transform = 'translateX(0)';
                                                }
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ color: '#F5F1ED', fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                                                        {houseType.name}
                                                    </div>
                                                    <div style={{ color: 'rgba(245, 241, 237, 0.5)', fontSize: '13px' }}>
                                                        {houseType.sqft}
                                                    </div>
                                                </div>
                                                <div style={{ color: 'var(--accent-color)', fontWeight: '600', fontSize: '15px' }}>
                                                    {houseType.range}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Square Feet Input */}
                            <div style={{ marginBottom: '30px' }}>
                                <label style={{ display: 'block', color: 'rgba(245, 241, 237, 0.9)', fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>
                                    Square Feet
                                </label>
                                <input
                                    type="number"
                                    value={squareFeet}
                                    onChange={(e) => setSquareFeet(e.target.value)}
                                    placeholder="Enter square feet"
                                    style={{
                                        width: '100%',
                                        padding: '15px 20px',
                                        background: 'rgba(10, 10, 10, 0.8)',
                                        border: '1px solid rgba(100, 100, 100, 0.3)',
                                        borderRadius: '10px',
                                        color: '#F5F1ED',
                                        fontSize: '16px',
                                        outline: 'none',
                                        transition: 'border-color 0.3s ease'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-color)'}
                                    onBlur={(e) => e.target.style.borderColor = 'rgba(100, 100, 100, 0.3)'}
                                />
                            </div>

                            {/* Total Budget Input */}
                            <div style={{ marginBottom: '40px' }}>
                                <label style={{ display: 'block', color: 'rgba(245, 241, 237, 0.9)', fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>
                                    Total Budget (₹ in Lakhs)
                                </label>
                                <input
                                    type="number"
                                    value={totalBudget}
                                    onChange={(e) => setTotalBudget(e.target.value)}
                                    placeholder="Enter your budget"
                                    style={{
                                        width: '100%',
                                        padding: '15px 20px',
                                        background: 'rgba(10, 10, 10, 0.8)',
                                        border: '1px solid rgba(100, 100, 100, 0.3)',
                                        borderRadius: '10px',
                                        color: '#F5F1ED',
                                        fontSize: '16px',
                                        outline: 'none',
                                        transition: 'border-color 0.3s ease'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-color)'}
                                    onBlur={(e) => e.target.style.borderColor = 'rgba(100, 100, 100, 0.3)'}
                                />
                            </div>

                            {/* Calculate Button */}
                            <button
                                onClick={calculateBudget}
                                style={{
                                    width: '100%',
                                    padding: '18px 40px',
                                    background: 'linear-gradient(135deg, var(--accent-color), #d4a574)',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 15px rgba(201, 169, 110, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 6px 25px rgba(201, 169, 110, 0.5)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(201, 169, 110, 0.3)';
                                }}
                            >
                                Calculate Budget Breakdown
                            </button>
                        </div>

                        {/* Right Side - Results */}
                        <div className="calculator-results-panel" style={{ 
                            background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.8), rgba(10, 10, 10, 0.9))',
                            border: '1px solid rgba(201, 169, 110, 0.2)',
                            borderRadius: '20px',
                            padding: '40px',
                            backdropFilter: 'blur(10px)'
                        }}>
                            <h3 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-color)', marginBottom: '40px' }}>
                                The 73% Solution
                            </h3>

                            {calculatedBreakdown ? (
                                <>
                                    {/* Coverage Summary */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.15), rgba(201, 169, 110, 0.05))',
                                        border: '1px solid rgba(201, 169, 110, 0.3)',
                                        borderRadius: '15px',
                                        padding: '30px',
                                        marginBottom: '30px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '48px', fontWeight: '800', color: 'var(--accent-color)', marginBottom: '10px' }}>
                                            73%
                                        </div>
                                        <div style={{ color: 'rgba(245, 241, 237, 0.8)', fontSize: '14px', marginBottom: '15px' }}>
                                            Covered by HAV'N
                                        </div>
                                        <div style={{ color: 'var(--accent-color)', fontSize: '24px', fontWeight: '700' }}>
                                            ₹{calculatedBreakdown.totalCovered} Lakhs
                                        </div>
                                    </div>

                                    {/* Breakdown List */}
                                    <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '10px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {calculatedBreakdown.items.map((item, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        padding: '15px 20px',
                                                        borderRadius: '10px',
                                                        border: item.covered 
                                                            ? '1px solid rgba(76, 175, 80, 0.3)' 
                                                            : '1px solid rgba(244, 67, 54, 0.3)',
                                                        background: item.covered 
                                                            ? 'rgba(76, 175, 80, 0.1)' 
                                                            : 'rgba(244, 67, 54, 0.1)',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(5px)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ color: '#F5F1ED', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                                                                {item.name}
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                                <span style={{ color: 'rgba(245, 241, 237, 0.5)', fontSize: '12px' }}>
                                                                    {item.percentage}%
                                                                </span>
                                                                <span style={{ 
                                                                    color: item.covered ? '#4CAF50' : '#F44336',
                                                                    fontSize: '12px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '5px'
                                                                }}>
                                                                    {item.covered ? (
                                                                        <>
                                                                            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                            </svg>
                                                                            We Cover
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                            </svg>
                                                                            External
                                                                        </>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div style={{ color: 'var(--accent-color)', fontWeight: '600', fontSize: '15px', whiteSpace: 'nowrap', marginLeft: '15px' }}>
                                                            ₹{item.cost}L
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    height: '400px',
                                    color: 'rgba(245, 241, 237, 0.5)',
                                    textAlign: 'center'
                                }}>
                                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="rgba(245, 241, 237, 0.3)" strokeWidth="2" style={{ marginBottom: '20px' }}>
                                        <line x1="18" y1="20" x2="18" y2="10"/>
                                        <line x1="12" y1="20" x2="12" y2="4"/>
                                        <line x1="6" y1="20" x2="6" y2="14"/>
                                    </svg>
                                    <p style={{ fontSize: '18px', lineHeight: '1.6' }}>
                                        Select your house type and budget<br/>
                                        to see the detailed breakdown
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Features */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px' }}>
                        <div style={{
                            textAlign: 'center',
                            padding: '30px',
                            background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.6), rgba(10, 10, 10, 0.8))',
                            border: '1px solid rgba(201, 169, 110, 0.2)',
                            borderRadius: '15px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.4)';
                            e.currentTarget.style.transform = 'translateY(-5px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        >
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" style={{ marginBottom: '15px', margin: '0 auto 15px' }}>
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                                <polyline points="9 22 9 12 15 12 15 22"/>
                            </svg>
                            <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#F5F1ED', marginBottom: '10px' }}>
                                Comprehensive Coverage
                            </h4>
                            <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.6)', lineHeight: '1.6' }}>
                                73% of your project requirements under one roof
                            </p>
                        </div>

                        <div style={{
                            textAlign: 'center',
                            padding: '30px',
                            background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.6), rgba(10, 10, 10, 0.8))',
                            border: '1px solid rgba(201, 169, 110, 0.2)',
                            borderRadius: '15px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.4)';
                            e.currentTarget.style.transform = 'translateY(-5px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        >
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" style={{ marginBottom: '15px', margin: '0 auto 15px' }}>
                                <line x1="12" y1="1" x2="12" y2="23"/>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                            <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#F5F1ED', marginBottom: '10px' }}>
                                Transparent Pricing
                            </h4>
                            <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.6)', lineHeight: '1.6' }}>
                                Clear breakdown of costs with no hidden charges
                            </p>
                        </div>

                        <div style={{
                            textAlign: 'center',
                            padding: '30px',
                            background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.6), rgba(10, 10, 10, 0.8))',
                            border: '1px solid rgba(201, 169, 110, 0.2)',
                            borderRadius: '15px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.4)';
                            e.currentTarget.style.transform = 'translateY(-5px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        >
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" style={{ marginBottom: '15px', margin: '0 auto 15px' }}>
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                            </svg>
                            <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#F5F1ED', marginBottom: '10px' }}>
                                Expert Guidance
                            </h4>
                            <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.6)', lineHeight: '1.6' }}>
                                Professional consultation for optimal budget allocation
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="services" id="services">
                <div className="services-split-container">
                    {/* Left Side - Image */}
                    <div className="services-image-side">
                        {Object.keys(services).map((key) => {
                            const serviceImages = {
                                planning: PLACEHOLDER_IMAGES.planning,
                                aether: PLACEHOLDER_IMAGES.lighting,
                                chisel: PLACEHOLDER_IMAGES.furniture,
                                thread: PLACEHOLDER_IMAGES.livingroom,
                                muse: PLACEHOLDER_IMAGES.design,
                                nexus: PLACEHOLDER_IMAGES.materials,
                                management: PLACEHOLDER_IMAGES.consultation
                            };

                            return (
                                <div 
                                    key={key} 
                                    className={`service-image-slide ${activeService === key ? 'active' : ''}`}
                                >
                                    <img 
                                        src={serviceImages[key]} 
                                        alt={services[key].title}
                                    />
                                    <div className="service-image-overlay"></div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Right Side - Content */}
                    <div className="services-content-side">
                        <div className="services-content-wrapper">
                            <div className="services-header">
                                <p className="services-label">What We Offer</p>
                                <h2 className="services-main-title">Our Services</h2>
                            </div>

                            <div className="services-slider">
                                {Object.keys(services).map((key, index) => (
                                    <div 
                                        key={key} 
                                        className={`service-slide ${activeService === key ? 'active' : ''}`}
                                    >
                                        <div className="service-number">0{index + 1}</div>
                                        <h3 className="service-slide-title">{services[key].title}</h3>
                                        <p className="service-slide-description">{services[key].description}</p>
                                        <ul className="service-slide-features">
                                            {services[key].features.slice(0, 4).map((feature, idx) => (
                                                <li key={idx}>
                                                    <span className="feature-icon">✓</span>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            {/* Navigation Dots */}
                            <div className="services-navigation">
                                {Object.keys(services).map((key, index) => (
                                    <button
                                        key={key}
                                        className={`service-nav-dot ${activeService === key ? 'active' : ''}`}
                                        onClick={() => setActiveService(key)}
                                        aria-label={`Go to ${services[key].title}`}
                                    >
                                        <span className="dot-number">0{index + 1}</span>
                                        <span className="dot-label">{services[key].title.split(' ')[0]}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Arrow Navigation */}
                            <div className="services-arrows">
                                <button 
                                    className="service-arrow prev"
                                    onClick={() => {
                                        const keys = Object.keys(services);
                                        const currentIndex = keys.indexOf(activeService);
                                        const prevIndex = currentIndex === 0 ? keys.length - 1 : currentIndex - 1;
                                        setActiveService(keys[prevIndex]);
                                    }}
                                >
                                    ←
                                </button>
                                <button 
                                    className="service-arrow next"
                                    onClick={() => {
                                        const keys = Object.keys(services);
                                        const currentIndex = keys.indexOf(activeService);
                                        const nextIndex = currentIndex === keys.length - 1 ? 0 : currentIndex + 1;
                                        setActiveService(keys[nextIndex]);
                                    }}
                                >
                                    →
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Blog & Instagram Section */}
            <section className="blog-instagram" id="blog">
                <div className="blog-instagram-header">
                    <h2 className="section-title">Stay Inspired</h2>
                    <p style={{ color: 'rgba(245, 241, 237, 0.6)', fontSize: '16px', marginTop: '20px', letterSpacing: '1px' }}>
                        Explore our latest insights, projects, and creative inspiration
                    </p>
                </div>

                <div className="blog-instagram-tabs">
                    <button 
                        className={`tab-button ${activeTab === 'blogs' ? 'active' : ''}`}
                        onClick={() => setActiveTab('blogs')}
                    >
                        Latest Blogs
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'instagram' ? 'active' : ''}`}
                        onClick={() => setActiveTab('instagram')}
                    >
                        Instagram Feed
                    </button>
                </div>

                <div className={`tab-content ${activeTab === 'blogs' ? 'active' : ''}`}>
                    <div className="blog-grid">
                        {latestBlogs.map(blog => (
                            <Link to={`/blog/${blog.slug}`} key={blog._id} className="blog-card">
                                <div className="blog-image-wrapper">
                                    <img src={blog.featuredImage} alt={blog.title} className="blog-image" />
                                </div>
                                <div className="blog-content">
                                    <p className="blog-date">
                                        {new Date(blog.publishedAt || blog.createdAt).toLocaleDateString('en-US', { 
                                            month: 'long', 
                                            day: 'numeric', 
                                            year: 'numeric' 
                                        }).toUpperCase()}
                                    </p>
                                    <h3 className="blog-title">{blog.title}</h3>
                                    <p className="blog-excerpt">
                                        {blog.excerpt || (blog.content ? blog.content.substring(0, 120) + '...' : '')}
                                    </p>
                                    <span className="blog-read-more">Read More →</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                <div className={`tab-content ${activeTab === 'instagram' ? 'active' : ''}`}>
                    <div className="instagram-grid">
                        {instagramPosts.slice(0, 6).map(post => (
                            <a 
                                href={post.permalink || 'https://instagram.com/havninteriors'} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                key={post.id || post.postId}
                                className="instagram-post"
                            >
                                <img src={post.imageUrl} alt={post.caption || 'HAV\'N Interiors'} className="instagram-image" />
                                <div className="instagram-overlay">
                                    <div className="instagram-info">
                                        <p>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                            </svg>
                                            {post.likes || 0}
                                        </p>
                                        <p>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                            </svg>
                                            {post.comments || 0}
                                        </p>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                    
                    <div className="instagram-cta">
                        <a href="https://instagram.com/havninteriors" target="_blank" rel="noopener noreferrer" className="instagram-follow-btn">
                            Follow Us on Instagram
                        </a>
                    </div>
                </div>
            </section>

            {/* Partner Category Section */}
            <section className="partner-category" id="partnership" style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${PLACEHOLDER_IMAGES.team})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}>
                <div className="partner-container">
                    <div className="partner-left">
                        <div className="partner-circle">
                            <h2>Partner<br/>Category</h2>
                        </div>
                    </div>
                    
                    <div className="partner-right">
                        <div 
                            className="partner-item partner-light" 
                            onClick={() => {
                                setSelectedPartnerCategory('distributors');
                                setShowPartnerCategoryModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="partner-tag">
                                <span>Distributors</span>
                            </div>
                        </div>
                        
                        <div 
                            className="partner-item partner-dark"
                            onClick={() => {
                                setSelectedPartnerCategory('cluster');
                                setShowPartnerCategoryModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="partner-tag">
                                <span>Cluster Distributors</span>
                            </div>
                        </div>
                        
                        <div 
                            className="partner-item partner-light"
                            onClick={() => {
                                setSelectedPartnerCategory('franchises');
                                setShowPartnerCategoryModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="partner-tag">
                                <span>Franchises</span>
                            </div>
                        </div>
                        
                        <div 
                            className="partner-item partner-dark"
                            onClick={() => {
                                setSelectedPartnerCategory('dealers');
                                setShowPartnerCategoryModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="partner-tag">
                                <span>Dealer</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div style={{ textAlign: 'center', marginTop: '60px' }}>
                    <p style={{ fontSize: '18px', color: 'rgba(245, 241, 237, 0.8)', marginBottom: '30px' }}>
                        Join our growing network of partners across India
                    </p>
                    <button 
                        onClick={() => setShowPartnerPopup(true)} 
                        className="partner-cta-btn"
                        style={{ border: 'none', cursor: 'pointer' }}
                    >
                        Become a Partner
                    </button>
                </div>
            </section>

            {/* Partner Form Popup */}
            {showPartnerPopup && (
                <div className="partner-popup-overlay" onClick={() => setShowPartnerPopup(false)}>
                    <div className="partner-popup-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="partner-popup-close"
                            onClick={() => setShowPartnerPopup(false)}
                        >
                            ✕
                        </button>
                        
                        <h2 className="partner-popup-title">Become a Partner</h2>
                        <p className="partner-popup-subtitle">Join our growing network and grow your business with us</p>
                        
                        {submitStatus === 'success' ? (
                            <div className="partner-success-message">
                                <div className="success-icon">✓</div>
                                <h3>Thank You!</h3>
                                <p>We've received your partnership inquiry. Our team will contact you within 24 hours.</p>
                                <button 
                                    onClick={() => {
                                        setShowPartnerPopup(false);
                                        setSubmitStatus(null);
                                        setPartnerForm({ name: '', phone: '', email: '', message: '' });
                                    }}
                                    className="success-close-btn"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form 
                                className="partner-form"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    setIsSubmitting(true);
                                    
                                    // Simulate API call
                                    setTimeout(() => {
                                        console.log('Partner Form Submitted:', partnerForm);
                                        setIsSubmitting(false);
                                        setSubmitStatus('success');
                                    }, 1500);
                                }}
                            >
                                <div className="form-group">
                                    <label htmlFor="partner-name">Full Name *</label>
                                    <input
                                        type="text"
                                        id="partner-name"
                                        value={partnerForm.name}
                                        onChange={(e) => setPartnerForm({...partnerForm, name: e.target.value})}
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="partner-phone">Phone Number *</label>
                                    <input
                                        type="tel"
                                        id="partner-phone"
                                        value={partnerForm.phone}
                                        onChange={(e) => setPartnerForm({...partnerForm, phone: e.target.value})}
                                        placeholder="Enter your phone number"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="partner-email">Email Address *</label>
                                    <input
                                        type="email"
                                        id="partner-email"
                                        value={partnerForm.email}
                                        onChange={(e) => setPartnerForm({...partnerForm, email: e.target.value})}
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="partner-message">Message</label>
                                    <textarea
                                        id="partner-message"
                                        value={partnerForm.message}
                                        onChange={(e) => setPartnerForm({...partnerForm, message: e.target.value})}
                                        placeholder="Tell us about your business and partnership interest"
                                        rows="4"
                                    />
                                </div>

                                <button 
                                    type="submit" 
                                    className="partner-submit-btn"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Partnership Inquiry'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Get Estimate Section */}
            <section className="get-estimate" id="estimate">
                <div className="estimate-container">
                    <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '20px' }}>Get Your Estimate in 3 Simple Steps</h2>
                    <p style={{ textAlign: 'center', color: 'rgba(245, 241, 237, 0.6)', fontSize: '16px', marginBottom: '80px', letterSpacing: '1px' }}>
                        Start your interior design journey with us today
                    </p>
                    
                    <div className="estimate-steps">
                        <div className="estimate-step">
                            <div className="step-icon">
                                <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="20" y="20" width="30" height="30" stroke="currentColor" strokeWidth="3"/>
                                    <rect x="55" y="20" width="25" height="30" stroke="currentColor" strokeWidth="3"/>
                                    <rect x="20" y="55" width="30" height="25" stroke="currentColor" strokeWidth="3"/>
                                    <rect x="55" y="55" width="25" height="25" stroke="currentColor" strokeWidth="3"/>
                                    <line x1="35" y1="15" x2="35" y2="20" stroke="currentColor" strokeWidth="3"/>
                                    <line x1="67" y1="15" x2="67" y2="20" stroke="currentColor" strokeWidth="3"/>
                                </svg>
                            </div>
                            <h3 className="step-title">Choose your BHK type and purpose</h3>
                            <p className="step-description">
                                The type of house and purpose (Move in, rent out, renovate), helps us understand your requirements better.
                            </p>
                        </div>
                        
                        <div className="estimate-step">
                            <div className="step-icon">
                                <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="25" y="20" width="50" height="60" rx="3" stroke="currentColor" strokeWidth="3"/>
                                    <circle cx="50" cy="35" r="10" stroke="currentColor" strokeWidth="2.5"/>
                                    <line x1="35" y1="52" x2="65" y2="52" stroke="currentColor" strokeWidth="2.5"/>
                                    <line x1="35" y1="60" x2="65" y2="60" stroke="currentColor" strokeWidth="2.5"/>
                                    <line x1="35" y1="68" x2="55" y2="68" stroke="currentColor" strokeWidth="2.5"/>
                                </svg>
                            </div>
                            <h3 className="step-title">Pick the rooms/areas of the house to be designed</h3>
                            <p className="step-description">
                                We'll be able to understand the scope of work for your interiors from this.
                            </p>
                        </div>
                        
                        <div className="estimate-step">
                            <div className="step-icon">
                                <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="30" y="35" width="40" height="30" rx="2" stroke="currentColor" strokeWidth="3"/>
                                    <rect x="25" y="40" width="50" height="3" fill="currentColor"/>
                                    <rect x="20" y="68" width="60" height="4" rx="1" fill="currentColor"/>
                                    <rect x="42" y="50" width="3" height="8" fill="currentColor"/>
                                    <rect x="55" y="50" width="3" height="8" fill="currentColor"/>
                                    <rect x="75" y="25" width="15" height="12" stroke="currentColor" strokeWidth="2.5"/>
                                    <line x1="77" y1="28" x2="88" y2="28" stroke="currentColor" strokeWidth="1.5"/>
                                    <circle cx="87" cy="33" r="2" fill="currentColor"/>
                                </svg>
                            </div>
                            <h3 className="step-title">Share contact and move-in details</h3>
                            <p className="step-description">
                                We can get in touch and better plan the design process for you.
                            </p>
                        </div>
                    </div>
                    
                    <div style={{ textAlign: 'center', marginTop: '60px' }}>
                        <Link to="/contact" className="estimate-cta-btn">Get Free Estimate</Link>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="contact" id="contact">
                <div className="contact-header">
                    <h2 className="section-title">Get in Touch</h2>
                    <p style={{ color: 'rgba(245, 241, 237, 0.6)', fontSize: '16px', marginTop: '20px', letterSpacing: '1px' }}>
                        Let's Create Something Beautiful Together
                    </p>
                </div>
                
                <div className="contact-container">
                    {/* Phone Numbers */}
                    <div className="contact-item">
                        <h3>Phone Numbers</h3>
                        <div className="contact-detail">
                            <strong>Land Line:</strong><br/>
                            <a href="tel:04442624855">044 42624855</a> <span style={{ color: 'rgba(245, 241, 237, 0.5)', fontSize: '13px' }}>(30 lines)</span>
                        </div>
                        <div className="contact-detail">
                            <strong>Project Coordinator:</strong><br/>
                            <a href="tel:+917825821201">78258 21201</a> / 
                            <a href="tel:+917290085871"> 72900 85871</a> / 
                            <a href="tel:+919884178180"> 98841 78180</a>
                        </div>
                        <div className="contact-detail">
                            <strong>HR Department:</strong><br/>
                            <span style={{ color: 'rgba(245, 241, 237, 0.6)' }}>Contact via email</span>
                        </div>
                    </div>

                    {/* Email Addresses */}
                    <div className="contact-item">
                        <h3>Email Addresses</h3>
                        <div className="contact-detail">
                            <strong>General Inquiries:</strong><br/>
                            <a href="mailto:admin@rbigroup.com">admin@rbigroup.com</a>
                        </div>
                        <div className="contact-detail">
                            <strong>Interior Design:</strong><br/>
                            <a href="mailto:haveninteriorsglobalsourcing@gmail.com">haveninteriorsglobalsourcing@gmail.com</a>
                        </div>
                        <div className="contact-detail">
                            <strong>HR Department:</strong><br/>
                            <span style={{ color: 'rgba(245, 241, 237, 0.6)' }}>Available on request</span>
                        </div>
                    </div>

                    {/* Locations */}
                    <div className="contact-item">
                        <h3>Visit Our Locations</h3>
                        <div className="contact-detail" style={{ marginBottom: '25px' }}>
                            <strong>Design Gallery</strong><br/>
                            <span style={{ color: 'rgba(245, 241, 237, 0.7)', fontSize: '14px' }}>
                                No 88/1, 88 Raja Muthiah Rd,<br/>
                                Opp to Nehru Stadium Periamet,<br/>
                                Choolai, Chennai - 600 003
                            </span>
                            <br/>
                            <a href="https://share.google/bVaj0Sm8KOs89LlSX" target="_blank" rel="noopener noreferrer" className="location-link">
                                View on Google Maps →
                            </a>
                        </div>
                        <div className="contact-detail">
                            <strong>Corporate Office</strong><br/>
                            <span style={{ color: 'rgba(245, 241, 237, 0.7)', fontSize: '14px' }}>
                                Bunglow No: 9/2 (5/1-B),<br/>
                                Letangs Road, Purasaiwakkam,<br/>
                                Chennai – 600007
                            </span>
                            <br/>
                            <a href="https://share.google/dvXA4MRLzKlXWROMN" target="_blank" rel="noopener noreferrer" className="location-link">
                                View on Google Maps →
                            </a>
                        </div>
                    </div>

                    {/* Social Media & Connect */}
                    <div className="contact-item">
                        <h3>Connect With Us</h3>
                        <p style={{ color: 'rgba(245, 241, 237, 0.6)', fontSize: '14px', marginBottom: '20px' }}>
                            Follow us on social media for updates, inspiration, and project showcases.
                        </p>
                        <div className="social-links">
                            <a href="#" className="social-link" title="WhatsApp">
                                WhatsApp
                            </a>
                            <a href="#" className="social-link" title="WeChat">
                                WeChat
                            </a>
                            <a href="#" className="social-link" title="Instagram">
                                Instagram
                            </a>
                            <a href="#" className="social-link" title="LinkedIn">
                                LinkedIn
                            </a>
                        </div>
                        <p style={{ color: 'var(--accent-color)', fontSize: '13px', marginTop: '25px', padding: '15px', background: 'rgba(201, 169, 110, 0.08)', borderRadius: '8px', borderLeft: '3px solid var(--accent-color)' }}>
                            <strong>24/7 Chat Support:</strong> Our chatbot is available to assist you anytime!
                        </p>
                    </div>
                </div>
            </section>

            {/* Studio Popup Modal */}
            {showStudioPopup && selectedStudio && (
                <div className={`studio-popup ${showStudioPopup ? 'active' : ''}`} onClick={closeStudioPopup}>
                    <div className="studio-popup-content" onClick={(e) => e.stopPropagation()}>
                        <button className="studio-popup-close" onClick={closeStudioPopup}>&times;</button>
                        <h2 className="studio-popup-title">{studioContent[selectedStudio].title}</h2>
                        <div 
                            className="studio-popup-body" 
                            dangerouslySetInnerHTML={{ __html: studioContent[selectedStudio].content }}
                        />
                        <div style={{ marginTop: '40px', textAlign: 'center', paddingTop: '30px', borderTop: '2px solid var(--accent-color)' }}>
                            <Link 
                                to="/studios" 
                                className="partner-cta-btn" 
                                onClick={closeStudioPopup}
                                style={{ display: 'inline-block' }}
                            >
                                View More
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" className="footer-logo" />
                <p>&copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.</p>
                <p style={{ marginTop: '10px', fontSize: '12px', opacity: 0.6 }}>Innovate. Integrate. Execute.</p>
            </footer>

            {/* Partner Category Modal */}
            <PartnerCategoryModal 
                isOpen={showPartnerCategoryModal}
                onClose={() => setShowPartnerCategoryModal(false)}
                category={selectedPartnerCategory}
            />
        </div>
    );
};

export default HomePage;
